import { ReflectionCard } from "../../domain/ReflectionCard";
import { MarkdownSectionHeading } from "./MarkdownSectionHeading";
import { hasJapaneseBoundary } from "../../../lib/japanese-word-boundary";
import {
  Children,
  type ComponentType,
  cloneElement,
  createContext,
  Fragment,
  isValidElement,
  useContext,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  Components,
  Options as ReactMarkdownOptions,
} from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import { NO_AUTOLINK_EVERYDAY_WORDS } from "./glossaryNoAutolink";
import { EXAMPLE_LABELS, remarkExampleKinds, remarkReadingTips, type ExampleKind } from "./remarkReadingTips";
import { remarkAsciiAutolink } from "./remarkAsciiAutolink";
import { remarkProseTagsAsText } from "./remarkProseTagsAsText";
import { remarkQuizAnswers } from "./remarkQuizAnswers";
import rehypeRaw from "rehype-raw";
import { rehypeConceptMarks, rehypeInlineLogos } from "./inline-logos/rehypeInlineLogos";
import { BLOCK_TAGS, rehypeLimitInlineMarks } from "./rehypeLimitInlineMarks";
import { GlossaryTermInline } from "./GlossaryTermCard";
import { rehypeSvgFigures } from "./rehypeSvgFigures";
import { rehypeJoinCjkAcrossInline } from "./rehypeJoinCjkAcrossInline";
import { rehypeKeepTail } from "./rehypeKeepTail";
import { rehypeKeepNumberUnits } from "./rehypeKeepNumberUnits";
import { rehypeKeepQuotes } from "./rehypeKeepQuotes";
import { rehypePhraseBreaks } from "./rehypePhraseBreaks";
import { rehypeSanitizeLessonHtml } from "./rehypeSanitizeLessonHtml";
import { visit } from "unist-util-visit";
import { CodeContainer } from "../CodeContainer";
import { useScrollMore } from "../../../hooks/useScrollMore";
import { RunnableCodeBlock, detectLanguage } from "../RunnableCodeBlock";
import { VideoEmbed } from "../VideoEmbed";
import { InteractiveChart } from "../InteractiveChart";
import { InteractiveFigure } from "../InteractiveFigure/InteractiveFigure";
import { OrangeSenseiIcon } from "../Icon";
import { OGPLinkCard } from "../OGPLinkCard";
import { escapeDunders } from "./dunder";
import {
  TweetEmbed,
  TweetEmbedFallback,
  tweetIdFromRef,
  type TweetEmbedMap,
} from "../TweetEmbed";
import { ExperienceBlock } from "../../domain/ExperienceBlock";
import { MermaidBlock } from "./MermaidBlock";
import {
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  HelpCircle,
  Lightbulb,
  Pencil,
} from "lucide-react";

type ReactMarkdownComponent = ComponentType<ReactMarkdownOptions>;

// 旧: lazy import で SSR は <div>Loading...</div> しか返さず、SEO 上記事本文が
// 検索エンジンに見えていなかった。同期 import に切り替えて SSR HTML を復活させる。
// Cloudflare Workers の bundle size / cold start には影響あるが、SEO / TTI の改善を優先。
import ReactMarkdownDefault from "react-markdown";
const ReactMarkdown = ReactMarkdownDefault as ReactMarkdownComponent;

// ==マーカーテキスト== を <mark> タグに変換する前処理
// **太字** を <strong> タグに変換（日本語文中でも動作するように）
// ✅ / ✏️ をアイコン用 <span> に変換（コードブロック内は除外）
type DivTag = {
  end: number;
  isClosing: boolean;
  isSelfClosing: boolean;
  source: string;
};

function readDivTagAt(content: string, start: number): DivTag | null {
  if (content[start] !== "<") return null;

  let cursor = start + 1;
  while (/\s/.test(content[cursor] ?? "")) cursor++;

  const isClosing = content[cursor] === "/";
  if (isClosing) {
    cursor++;
    while (/\s/.test(content[cursor] ?? "")) cursor++;
  }

  if (content.slice(cursor, cursor + 3).toLowerCase() !== "div") return null;
  cursor += 3;
  if (!/[\s/>]/.test(content[cursor] ?? "")) return null;

  let quote: '"' | "'" | null = null;
  for (; cursor < content.length; cursor++) {
    const char = content[cursor];
    if (quote) {
      if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char !== ">") continue;

    let beforeClose = cursor - 1;
    while (/\s/.test(content[beforeClose] ?? "")) beforeClose--;
    return {
      end: cursor + 1,
      isClosing,
      isSelfClosing: !isClosing && content[beforeClose] === "/",
      source: content.slice(start, cursor + 1),
    };
  }

  return null;
}

function findLessonDiagramEnd(
  content: string,
  openingTag: DivTag,
): number | null {
  let depth = 1;
  let cursor = openingTag.end;

  while (cursor < content.length) {
    const tagStart = content.indexOf("<", cursor);
    if (tagStart === -1) return null;

    const tag = readDivTagAt(content, tagStart);
    if (!tag) {
      cursor = tagStart + 1;
      continue;
    }

    if (tag.isClosing) depth--;
    else if (!tag.isSelfClosing) depth++;

    if (depth === 0) return tag.end;
    cursor = tag.end;
  }

  return null;
}

function mapFigureBlocks(
  content: string,
  attrPattern: RegExp,
  transform: (block: string) => string,
): string {
  let result = "";
  let cursor = 0;

  while (cursor < content.length) {
    const tagStart = content.indexOf("<", cursor);
    if (tagStart === -1) break;

    const openingTag = readDivTagAt(content, tagStart);
    if (
      !openingTag ||
      openingTag.isClosing ||
      openingTag.isSelfClosing ||
      !attrPattern.test(openingTag.source)
    ) {
      result += content.slice(cursor, tagStart + 1);
      cursor = tagStart + 1;
      continue;
    }

    const diagramEnd = findLessonDiagramEnd(content, openingTag);
    if (diagramEnd === null) {
      result += content.slice(cursor, openingTag.end);
      cursor = openingTag.end;
      continue;
    }

    result += content.slice(cursor, tagStart);
    result += transform(content.slice(tagStart, diagramEnd));
    cursor = diagramEnd;
  }

  return result + content.slice(cursor);
}

function compactLessonDiagramHtml(content: string): string {
  return mapFigureBlocks(content, /\bdata-lesson-diagram\b/i, (block) =>
    block.replace(/\r?\n[ \t]*\r?\n/g, "\n"),
  );
}

/**
 * 強調の印 (== と **) を前から 2 つ 1 組で対にして、開く札と閉じる札に替える。
 * 対にするのは 1 つの段落 (空行で区切る) の中だけ。印の数が奇数の段落があっても、次の段落の対がずれない。
 * 3 つ以上並んだ印 (*** や ===) は、数えずそのまま残す。字として書いた「*** Please tell me who you are」で後ろの太字が崩れない。
 * 印の内側には、1 つだけの = や * が入ってよい (「==1 コミット = 1 意図==」「==temperature=0 + JSON モード==」)
 */
function pairMarks(body: string, mark: "==" | "**", open: string, close: string): string {
  const char = mark[0] === "=" ? "=" : "*";
  const token = new RegExp(`(?<!\\${char})\\${char}\\${char}(?!\\${char})`, "g");
  return body
    .split(/(\r?\n[ \t]*\r?\n)/)
    .map((part, index) => {
      if (index % 2 === 1) return part;
      const at = [...part.matchAll(token)].map((m) => m.index ?? 0);
      let out = "";
      let from = 0;
      for (let i = 0; i + 1 < at.length; i += 2) {
        const inner = part.slice(at[i] + 2, at[i + 1]);
        if (inner.trim() === "") continue;
        out += part.slice(from, at[i]) + open + inner + close;
        from = at[i + 1] + 2;
      }
      return out + part.slice(from);
    })
    .join("");
}

function preprocessHighlight(content: string): string {
  // コードブロック / インラインコード / 図の塊を一時的に退避する。
  // 図の中の字に == や ** が並んでも強調に変えない。
  // 印は本文に現れない英数の並びにし、本文の語と衝突させない。
  const placeholders: string[] = [];
  let marker = "MDSTASH9c1e7a";
  while (content.includes(marker)) marker += "z";
  const markerRe = new RegExp(`${marker}(\\d+)X`, "g");
  const stash = (s: string) => {
    placeholders.push(s);
    return `${marker}${placeholders.length - 1}X`;
  };
  let body = content.replace(/```[\s\S]*?```/g, (m) => stash(m));
  body = mapFigureBlocks(
    body,
    /\bdata-(?:lesson-diagram|zukai-figure)\b/i,
    (block) =>
      stash(
        /\bdata-lesson-diagram\b/i.test(block.slice(0, block.indexOf(">") + 1))
          ? block.replace(/\r?\n[ \t]*\r?\n/g, "\n")
          : block,
      ),
  );
  body = body.replace(/`[^`\n]+`/g, (m) => stash(m));

  body = pairMarks(pairMarks(escapeDunders(body), "==", '<mark class="highlight-green">', "</mark>"), "**", "<strong>", "</strong>")
  body = body
    .replace(/✅/g, '<span data-icon="check"></span>')
    .replace(/✏️/g, '<span data-icon="pencil"></span>');

  // 退避したものを戻す。退避した塊が別の印を含むことがあるので、印が無くなるまで繰り返す
  // (印は後から退避した塊ほど外側にあり、1 回に 1 段ずつ内側へ戻る)
  for (let pass = 0; pass <= placeholders.length && body.includes(marker); pass++) {
    body = body.replace(markerRe, (_, i) => placeholders[Number(i)]);
  }
  return body;
}

// 用語ハイライト用のエントリ型 (apps/client が辞書を組み立てて渡す)
export type GlossaryEntry = {
  /** メイン用語名 */
  term: string;
  /** ふりがな・読み方 (例: "Servlet" → "サーブレット") */
  reading?: string;
  /** マッチ対象のエイリアス (大文字小文字無視で照合) */
  aliases: string[];
  /** 1 行イメージ (popover の冒頭に表示) */
  summary: string;
  /** 詳細本文 (markdown 軽量レンダリング) */
  detail?: string;
  /** 関連章 (popover 末尾に表示) */
  related?: string;
  /**
   * 手で選んだ関連用語 (term か aliases の名前、書いた順)。
   * あるときは窓の「関連用語」にこれだけを出し、章の重なりでは選ばない。
   * 無い語だけ、今までどおり関連章の重なりで機械的に選ぶ。
   */
  relatedTerms?: string[];
  /** カテゴリ (例: "DB", "認証", "ネットワーク") */
  category?: string;
  /** 難易度 1=初級 / 2=中級 / 3=上級 */
  difficulty?: 1 | 2 | 3;
  /**
   * 公開用語集ページ (/glossary/:slug) の slug。
   * DB `glossary_entries` に存在する用語のみ slug を持つ。
   * 設定時は本文中の用語と tooltip 内の「詳しく見る」が詳細ページへリンクする。
   */
  slug?: string;
};

// =====================================================================
// Aho-Corasick automaton 型: 1500 語クラスでも 1 パススキャンで全マッチ取得
// goto[state][char] -> nextState, fail[state] -> suffixLink, output[state] -> [keys]
// =====================================================================
type AhoCorasick = {
  goto: Array<Map<number, number>>;
  fail: Int32Array;
  output: Array<string[] | null>;
};

// 用語マッチング用に辞書を flatten しておく
type GlossaryMatcher = {
  /** 小文字キー → エントリ */
  byKey: Map<string, GlossaryEntry>;
  /** 長い順にソートされたキー (前方一致でハマり防止) */
  sortedKeys: string[];
  /** Aho-Corasick オートマトン (1パスで全マッチ取得) */
  automaton: AhoCorasick;
};

function buildAhoCorasick(keys: string[]): AhoCorasick {
  const goto: Array<Map<number, number>> = [new Map()];
  const output: Array<string[] | null> = [null];

  // Trie 構築
  for (const key of keys) {
    let s = 0;
    for (let i = 0; i < key.length; i++) {
      const c = key.charCodeAt(i);
      const next = goto[s].get(c);
      if (next === undefined) {
        goto.push(new Map());
        output.push(null);
        const ns = goto.length - 1;
        goto[s].set(c, ns);
        s = ns;
      } else {
        s = next;
      }
    }
    if (!output[s]) output[s] = [];
    output[s]!.push(key);
  }

  // fail link を BFS で構築
  const fail = new Int32Array(goto.length);
  const queue: number[] = [];
  for (const [, child] of goto[0]) {
    fail[child] = 0;
    queue.push(child);
  }

  while (queue.length > 0) {
    const r = queue.shift()!;
    for (const [c, u] of goto[r]) {
      queue.push(u);
      let v = fail[r];
      while (v !== 0 && !goto[v].has(c)) v = fail[v];
      const dest = goto[v].get(c);
      fail[u] = dest !== undefined && dest !== u ? dest : 0;
      // dictionary suffix link をマージ
      const fOut = output[fail[u]];
      if (fOut) {
        if (output[u]) output[u]!.push(...fOut);
        else output[u] = [...fOut];
      }
    }
  }

  return { goto, fail, output };
}

/** 前の描き手で既に付いた印。terms は用語の点線を付けた語 (GlossaryEntry.term)、marks は語の前の印の鍵 */
export type InlineMarksSeen = { terms?: readonly string[]; marks?: readonly string[] };

function withoutSeenTerms(entries: GlossaryEntry[] | undefined, terms: readonly string[] | undefined): GlossaryEntry[] | undefined {
  if (!entries || !terms || terms.length === 0) return entries;
  const drop = new Set(terms);
  return entries.filter((e) => !drop.has(e.term));
}

const lineMatcherCache = new WeakMap<GlossaryEntry[], GlossaryMatcher | null>();

/**
 * Markdown (項目の頁のゴールの 1 行、クイズの前の本文など) に付く印を、描かずに数える。木は組まず字だけで当てるので、短い行と、前の描き手の分を数える用に使う。
 * 項目の頁はゴールの行と本文を別々の描き手で描くので、前の行で付いた印をここで数え、次の描き手の marksSeen に渡す
 * (「ファイル」が出るたびに印と点線が付き、本文がうるさかった。10/22 の点検)。
 * 行の中のコード (`cp`) とリンクの字には描き手も印を付けないので、ここでも外して数える
 */
export function inlineMarksOfLine(markdown: string, glossary: GlossaryEntry[] | undefined, seen?: InlineMarksSeen): { terms: string[]; marks: string[] } {
  const text = markdown
    // コードの囲み、見出しの行、参照リンクの定義 ([guide]: /guide) は描き手も印を付けない (定義は字として出ない)
    .replace(/^(```|~~~)[\s\S]*?^\1/gm, " ")
    .replace(/^#{1,6}\s.*$/gm, " ")
    .replace(/^\s*\[[^\]]+\]:\s.*$/gm, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\[[^\]]*\]/g, " ")
    .replace(/\*\*|__/g, "");
  const tree: any = { type: "root", children: [{ type: "element", tagName: "p", properties: {}, children: [{ type: "text", value: text }] }] };
  // 照合の表は辞書ごとに 1 回だけ組む (行ごとに 1000 語の表を組み直さない)。前に付いた語は、付けたあとで数から外す
  let matcher: GlossaryMatcher | null = null;
  if (glossary) {
    if (!lineMatcherCache.has(glossary)) lineMatcherCache.set(glossary, buildGlossaryMatcher(glossary));
    matcher = lineMatcherCache.get(glossary) ?? null;
  }
  if (matcher) rehypeGlossary({ matcher, allEntries: glossary })(tree);
  const seenTerms = new Set(seen?.terms ?? []);
  const marks = new Set<string>();
  rehypeConceptMarks({ seen: seen?.marks, collect: marks, scope: "page" })(tree);
  const terms: string[] = [];
  visit(tree, "element", (node: any) => {
    const term = node.tagName === "glossary-term" ? node.properties?.term : undefined;
    if (term && !seenTerms.has(String(term))) terms.push(String(term));
  });
  return { terms, marks: [...marks] };
}

function buildGlossaryMatcher(
  entries: GlossaryEntry[] | undefined,
): GlossaryMatcher | null {
  if (!entries || entries.length === 0) return null;
  const byKey = new Map<string, GlossaryEntry>();
  for (const e of entries) {
    for (const a of e.aliases.length > 0 ? e.aliases : [e.term]) {
      const k = a.toLowerCase();
      if (NO_AUTOLINK_EVERYDAY_WORDS.has(k)) continue;
      if (!byKey.has(k)) byKey.set(k, e);
    }
  }
  const sortedKeys = Array.from(byKey.keys()).sort(
    (a, b) => b.length - a.length,
  );
  const automaton = buildAhoCorasick(sortedKeys);
  return { byKey, sortedKeys, automaton };
}

/*
日本語の語の切れ目の判定は lib/japanese-word-boundary.ts に置いてある。
**用語集のリンクを作る場所が本文と巻末の2か所あり、同じ規則を2つ書くと
片方だけ直したときに食い違う。** 実際に、本文側だけ直して巻末側が
「アセット」の中の「セット」を拾い続けていた
*/

// 「出した語」の表に、用語そのもの (別名でなく見出し語) を入れる鍵。照合の鍵 (小文字の別名) と混ざらない形
const entryMark = (entry: GlossaryEntry) => `\u0000${entry.term}`;

// テキスト中の用語を 1 パスで検出。同一段落内で 1 回だけマークする。
function findGlossaryHits(
  text: string,
  matcher: GlossaryMatcher,
  seen: Set<string>,
  limit = Infinity,
): Array<{ start: number; end: number; matched: string; entry: GlossaryEntry }> {
  if (!text || limit <= 0) return [];
  const { automaton, byKey } = matcher;
  if (!automaton) return [];
  const { goto, fail, output } = automaton;

  // テキストを lowercase に1回だけ落とす
  const lower = text.toLowerCase();
  const len = lower.length;

  // 1パス Aho-Corasick: 全候補マッチを集める
  type Candidate = { start: number; end: number; key: string };
  const candidates: Candidate[] = [];
  let state = 0;
  for (let i = 0; i < len; i++) {
    const c = lower.charCodeAt(i);
    while (state !== 0 && !goto[state].has(c)) state = fail[state];
    state = goto[state].get(c) ?? 0;
    const out = output[state];
    if (out) {
      for (const key of out) {
        candidates.push({ start: i - key.length + 1, end: i + 1, key });
      }
    }
  }

  if (candidates.length === 0) return [];

  // 最長一致優先 + 重複領域除外
  // 同じ start なら長い key を優先、別 start なら左側の長いマッチを優先
  candidates.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    return b.end - a.end;
  });

  const hits: Array<{
    start: number;
    end: number;
    matched: string;
    entry: GlossaryEntry;
  }> = [];
  const consumed = new Uint8Array(len);

  for (const cand of candidates) {
    // 上限に達したら、残りの語は印を付けず「出した語」にも入れない (同じ節の次の段落で付く)
    if (hits.length >= limit) break;
    if (seen.has(cand.key)) continue;

    // 単語境界（ASCII 単語の場合）
    const isAsciiWord = /^[\x21-\x7e]+$/.test(cand.key);
    if (isAsciiWord) {
      const before = cand.start === 0 ? "" : text[cand.start - 1];
      const after = cand.end >= text.length ? "" : text[cand.end];
      if (/[A-Za-z0-9_]/.test(before)) continue;
      if (/[A-Za-z0-9_]/.test(after)) continue;
    } else if (!hasJapaneseBoundary(text, cand.start, cand.end)) {
      continue;
    }

    // 既に消費された領域は除外
    let blocked = false;
    for (let i = cand.start; i < cand.end; i++) {
      if (consumed[i]) {
        blocked = true;
        break;
      }
    }
    if (blocked) continue;

    const entry = byKey.get(cand.key);
    if (!entry) continue;
    // 同じ用語の別の書き方 (ルーター / ルータ) も、範囲の中で 1 回にする
    if (seen.has(entryMark(entry))) continue;

    hits.push({
      start: cand.start,
      end: cand.end,
      matched: text.slice(cand.start, cand.end),
      entry,
    });
    for (let i = cand.start; i < cand.end; i++) consumed[i] = 1;
    seen.add(cand.key);
    seen.add(entryMark(entry));
  }

  return hits;
}

// 用語ごとの関連章の番号 (Ch7-3 や Ch10)。同じ辞書を何度も走査するので、1 度だけ取り出して持つ
const chapterCache = new WeakMap<GlossaryEntry, string[]>();
function chaptersOf(entry: GlossaryEntry): string[] {
  let chapters = chapterCache.get(entry);
  if (!chapters) {
    chapters = entry.related?.match(/Ch[0-9-]+/g) ?? [];
    chapterCache.set(entry, chapters);
  }
  return chapters;
}

// 辞書の名前 (term と aliases、小文字) から語を引く表。同じ辞書を何度も走査するので、辞書ごとに 1 度だけ作る
const nameIndexCache = new WeakMap<GlossaryEntry[], Map<string, GlossaryEntry>>();
function nameIndexOf(allEntries: GlossaryEntry[]): Map<string, GlossaryEntry> {
  let index = nameIndexCache.get(allEntries);
  if (!index) {
    index = new Map();
    for (const e of allEntries) {
      for (const name of [e.term, ...(e.aliases ?? [])]) {
        const k = name.toLowerCase();
        if (!index.has(k)) index.set(k, e);
      }
    }
    nameIndexCache.set(allEntries, index);
  }
  return index;
}

// 窓の札は "用語@@slug" の形 (札が用語の頁へ飛ぶため)。slug が無ければ用語だけ
function relatedLabel(e: GlossaryEntry): string {
  return e.slug ? `${e.term}@@${e.slug}` : e.term;
}

// 関連用語サジェスト。
// 手で選んだ relatedTerms があれば、書いた順にそれだけを返す (辞書に無い名前と自分自身は飛ばす)。
// 1 つも引けなくても章の重なりには戻らない。足すと CPU に SQL が戻ってくるため。
// 無い語は、同じ "関連章" を持つ別エントリを、共有する章が多い順に最大 N 件
// (同じ数なら押せる (slug を持つ) 用語が先)
export function findRelatedTerms(
  entry: GlossaryEntry,
  allEntries: GlossaryEntry[],
  max = 4,
): string[] {
  if (entry.relatedTerms && entry.relatedTerms.length > 0) {
    const index = nameIndexOf(allEntries);
    const picked: GlossaryEntry[] = [];
    for (const name of entry.relatedTerms) {
      const hit = index.get(name.toLowerCase());
      if (!hit || hit === entry || picked.includes(hit)) continue;
      picked.push(hit);
    }
    return picked.slice(0, max).map(relatedLabel);
  }
  const mine = chaptersOf(entry);
  if (mine.length === 0) return [];
  const scored: Array<{ e: GlossaryEntry; shared: number; order: number }> = [];
  allEntries.forEach((e, order) => {
    if (e.term === entry.term) return;
    const theirs = chaptersOf(e);
    if (theirs.length === 0) return;
    let shared = 0;
    for (const c of mine) if (theirs.includes(c)) shared++;
    if (shared > 0) scored.push({ e, shared, order });
  });
  scored.sort(
    (a, b) =>
      b.shared - a.shared ||
      Number(Boolean(b.e.slug)) - Number(Boolean(a.e.slug)) ||
      a.order - b.order,
  );
  return scored.slice(0, max).map(({ e }) => relatedLabel(e));
}

// rehype プラグイン: text ノードを用語スパンに分割
function rehypeGlossary(options: {
  matcher: GlossaryMatcher | null;
  allEntries?: GlossaryEntry[];
  scope?: "page" | "section";
  /** 1 つの段落 (BLOCK_TAGS) に付ける用語の上限。無ければ上限なし */
  maxPerBlock?: number;
}) {
  return (tree: any) => {
    if (!options.matcher) return;
    const matcher = options.matcher;
    const allEntries = options.allEntries ?? [];
    const seen = new Set<string>(); // 範囲 (既定は頁全体、scope が section なら h2 の節) で 1 用語 1 回
    const perSection = options.scope === "section";
    const maxPerBlock = options.maxPerBlock ?? Infinity;
    let inBlock = 0; // いまの段落で付けた語の数
    const termElement = (entry: GlossaryEntry, matched: string) => ({
      type: "element",
      tagName: "glossary-term",
      properties: {
        term: entry.term,
        matched,
        summary: entry.summary,
        related: entry.related ?? "",
        detail: entry.detail ?? "",
        reading: entry.reading ?? "",
        category: entry.category ?? "",
        difficulty: entry.difficulty ? String(entry.difficulty) : "",
        slug: entry.slug ?? "",
        aliases: (entry.aliases ?? []).join("|"),
        "related-terms": findRelatedTerms(entry, allEntries).join("|"),
      },
      children: [{ type: "text", value: matched }],
    });
    let bySlug: Map<string, GlossaryEntry> | null = null;
    // 本文のリンクが自分たちの用語集の同じ語を指しているとき、その語の項目を返す
    const ownGlossaryEntry = (a: any): GlossaryEntry | null => {
      const href = String(a.properties?.href ?? "");
      const m = /^\/glossary\/([^/?#]+)\/?(?:[?#].*)?$/.exec(href);
      const kids = a.children ?? [];
      if (!m || kids.length !== 1 || kids[0].type !== "text") return null;
      if (!bySlug) {
        bySlug = new Map();
        for (const e of matcher.byKey.values()) if (e.slug) bySlug.set(e.slug, e);
      }
      let slug = m[1];
      try {
        slug = decodeURIComponent(slug);
      } catch {
        /* 壊れた % はそのまま照合して外れる */
      }
      const entry = bySlug.get(slug);
      if (!entry) return null;
      const label = String(kids[0].value).trim().toLowerCase();
      return matcher.byKey.get(label) === entry ? entry : null;
    };
    let totalMatches = 0;
    let visitedTextNodes = 0;
    const start = typeof performance !== "undefined" ? performance.now() : 0;
    const skipParents = new Set([
      "code",
      "pre",
      "h1",
      "h2",
      "h3",
      "h4",
      "a",
      "kbd",
      "mark",
      "style",
      "script",
      "svg",
      "math",
      "textarea",
    ]);

    const visitNode = (node: any, parent: any) => {
      if (!node) return;
      // h2 は走査の対象外 (skipParents) だが、節の切れ目として「出した語」を忘れる
      if (perSection && node.type === "element" && node.tagName === "h2") seen.clear();
      if (node.type === "element") {
        const properties = node.properties ?? {};
        if (
          "dataLessonDiagram" in properties ||
          "data-lesson-diagram" in properties ||
          "dataNoGlossary" in properties ||
          "data-no-glossary" in properties
        ) {
          return; // CSS・SVG・図中ラベルを本文の用語注釈で書き換えない
        }
      }
      // リンクの中の字には印を付けない (リンクの線と用語の点線が二重になる)。
      // ただし用語集の同じ語を指すリンクは、リンクのまま残さず用語の札に置き換える (その場で意味が読める)
      if (node.type === "element" && node.tagName === "a") {
        const entry = ownGlossaryEntry(node);
        if (entry && !seen.has(entryMark(entry)) && inBlock < maxPerBlock) {
          const matched = String(node.children[0].value);
          const t = termElement(entry, matched);
          node.tagName = t.tagName;
          // 字に戻す経路 (SSR と hydrate 前) でリンクを失わないよう、元がリンクだった印を残す
          node.properties = { ...t.properties, "from-link": "1" };
          node.children = t.children;
          seen.add(entryMark(entry));
          totalMatches++;
          inBlock++;
        }
        return;
      }
      if (
        node.type === "element" &&
        skipParents.has(String(node.tagName).toLowerCase())
      ) {
        return; // skip subtree
      }
      // 用語タグ自身もスキップ
      if (node.type === "element" && node.tagName === "glossary-term") return;

      if (node.type === "element" && Array.isArray(node.children)) {
        const isBlock = BLOCK_TAGS.has(String(node.tagName).toLowerCase());
        const outerCount = inBlock;
        if (isBlock) inBlock = 0;
        // 子要素を訪問しつつ、必要なら text を分割
        const newChildren: any[] = [];
        for (const child of node.children) {
          if (child.type === "text" && typeof child.value === "string") {
            visitedTextNodes++;
            const hits = findGlossaryHits(child.value, matcher, seen, maxPerBlock - inBlock);
            totalMatches += hits.length;
            inBlock += hits.length;
            if (hits.length === 0) {
              newChildren.push(child);
            } else {
              let cursor = 0;
              for (const hit of hits) {
                if (hit.start > cursor) {
                  newChildren.push({
                    type: "text",
                    value: child.value.slice(cursor, hit.start),
                  });
                }
                newChildren.push(termElement(hit.entry, hit.matched));
                cursor = hit.end;
              }
              if (cursor < child.value.length) {
                newChildren.push({
                  type: "text",
                  value: child.value.slice(cursor),
                });
              }
            }
          } else {
            visitNode(child, node);
            newChildren.push(child);
          }
        }
        node.children = newChildren;
        if (isBlock) inBlock = outerCount;
      } else if (node.type === "root" && Array.isArray(node.children)) {
        for (const child of node.children) {
          visitNode(child, node);
        }
      }
    };

    visitNode(tree, null);
  };
}

/**
 * SSR とマウント直前の描画では、用語集の下線を付けない (hydration の食い違い回避)。
 * ただし考え方の印の位置は、下線が付いたあとの木で決める必要がある
 * (「仮想メモリ」が用語集の語なら、その中の「メモリ」には印を付けない)。
 * そこで木の上では用語集を通し、印を決めたあとにこの関数で glossary-term を字に戻す。
 * 戻した字は隣の字と 1 つにつなぐ。
 */
function rehypeUnwrapGlossary() {
  return (tree: any) => {
    const walk = (node: any) => {
      if (!Array.isArray(node.children)) return;
      const next: any[] = [];
      const push = (c: any) => {
        const last = next[next.length - 1];
        if (c.type === "text" && last && last.type === "text") {
          next[next.length - 1] = { ...last, value: last.value + c.value };
        } else next.push(c);
      };
      for (const c of node.children) {
        if (c.type === "element" && c.tagName === "glossary-term") {
          if (c.properties?.["from-link"] && c.properties?.slug) {
            // 本文でリンクだった語は、字でなくリンクに戻す
            next.push({
              type: "element",
              tagName: "a",
              properties: { href: `/glossary/${encodeURIComponent(String(c.properties.slug))}` },
              children: c.children ?? [],
            });
            continue;
          }
          for (const g of c.children ?? []) push(g);
        } else {
          walk(c);
          push(c);
        }
      }
      node.children = next;
    };
    walk(tree);
  };
}

// カスタムディレクティブをHTMLノードに変換するプラグイン
/**
 * AST レベルで `==text==` を <mark> ノードに変換する remark プラグイン。
 *
 * `preprocessHighlight` を使えない (= offset を元 content と一致させたい)
 * インライン編集モードのための代替手段。preprocess は文字列置換で位置がズレる
 * ため、こちらは AST のテキストノードを分解してマーク種別を作る。
 *
 * 検出パターン: ==非空白テキスト==
 * code / inlineCode の中は visit 対象にしないので干渉しない。
 */
function remarkMarkSyntax() {
  return (tree: any) => {
    visit(tree, "text", (node: any, index: any, parent: any) => {
      if (!parent || typeof node.value !== "string" || index == null) return;
      const re = /==([^=]+?)==/g;
      const value: string = node.value;
      if (!re.test(value)) return;
      re.lastIndex = 0;
      const newNodes: any[] = [];
      let lastIdx = 0;
      let m: RegExpExecArray | null;
      while ((m = re.exec(value)) !== null) {
        if (m.index > lastIdx) {
          newNodes.push({ type: "text", value: value.slice(lastIdx, m.index) });
        }
        newNodes.push({
          type: "html",
          value: `<mark class="highlight-green">${m[1]}</mark>`,
        });
        lastIdx = m.index + m[0].length;
      }
      if (lastIdx < value.length) {
        newNodes.push({ type: "text", value: value.slice(lastIdx) });
      }
      parent.children.splice(index, 1, ...newNodes);
      return index + newNodes.length;
    });
  };
}

/**
 * 各 top-level block (paragraph, heading, list, blockquote, code,
 * containerDirective 等) に data-md-block-index="N" を付与する remark プラグイン。
 *
 * インライン編集 (`MarkdownContent` の `enableSourceMap` プロップ) で
 * 「クリックされたブロックの Markdown ソース範囲」を逆引きするために使う。
 *
 * 副作用として onBuilt コールバック経由でブロック→[startOffset, endOffset, source] のマップを返す。
 */
export type MarkdownBlockSource = {
  index: number;
  startOffset: number;
  endOffset: number;
  source: string;
  /** mdast node type (例: "paragraph", "heading", "containerDirective") */
  nodeType: string;
};

function remarkBlockSourceMap(options: {
  source: string;
  onBuilt?: (blocks: MarkdownBlockSource[]) => void;
}) {
  return (tree: any) => {
    const blocks: MarkdownBlockSource[] = [];
    const children = (tree?.children ?? []) as any[];
    children.forEach((node, i) => {
      const pos = node?.position;
      const start = pos?.start?.offset;
      const end = pos?.end?.offset;
      if (typeof start !== "number" || typeof end !== "number") return;
      const data = node.data || (node.data = {});
      const hProps = data.hProperties || (data.hProperties = {});
      hProps["data-md-block-index"] = String(i);
      blocks.push({
        index: i,
        startOffset: start,
        endOffset: end,
        source: options.source.slice(start, end),
        nodeType: node.type,
      });
    });
    options.onBuilt?.(blocks);
  };
}

// コードフェンスの言語エイリアスを executor が認識する正規名に変換する。
// ```js ``` や ```py ``` のような短縮形が runnable executor 側で
// "未対応の言語" になるのを防ぐ。未知のものはそのまま小文字で返す。
//
// 対応する executor は apps/client/app/lib/code-execution.ts:
//   javascript / typescript / python / java / go
export function normalizeLanguageAlias(lang: string | undefined | null): string | undefined {
  if (!lang) return undefined;
  const lower = lang.trim().toLowerCase();
  if (!lower) return undefined;
  const map: Record<string, string> = {
    js: "javascript",
    jsx: "javascript",
    javascript: "javascript",
    ts: "typescript",
    tsx: "typescript",
    typescript: "typescript",
    py: "python",
    python: "python",
    python3: "python",
    sh: "bash",
    shell: "bash",
    bash: "bash",
    "c++": "cpp",
    cpp: "cpp",
    cs: "csharp",
    csharp: "csharp",
    rb: "ruby",
    ruby: "ruby",
    golang: "go",
    go: "go",
    java: "java",
  };
  return map[lower] ?? lower;
}

// :::runnable ブロックの中身を source から順番に抽出する。
// MarkdownContent から useMemo で外出しした結果を runnableSources オプションで
// 渡せるよう、両方の入力形式を受け付ける (後方互換)。
//
// 戻り値は { code, language } の配列。
// 内側に ```lang ... ``` のコードフェンスが含まれている場合は剥がして言語も拾う。
// 言語名は normalizeLanguageAlias で executor が認識する正規名に揃える。
export type RunnableSource = { code: string; language?: string };
export function extractRunnableSources(source: string): RunnableSource[] {
  if (!source) return [];
  const out: RunnableSource[] = [];
  const re = /^:::runnable(?:\[[^\]]*\])?\s*\n([\s\S]*?)\n:::\s*$/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(source)) !== null) {
    const inner = m[1];
    // 内側にコードフェンス ```lang ... ``` があれば剥がす
    const fenceMatch = inner.match(
      /^```([A-Za-z0-9_+\-]*)\s*\n([\s\S]*?)\n```\s*$/
    );
    if (fenceMatch) {
      out.push({
        code: fenceMatch[2],
        language: normalizeLanguageAlias(fenceMatch[1]),
      });
    } else {
      out.push({ code: inner });
    }
  }
  return out;
}

// remark-directive が解釈するディレクティブのうち、実際に
// `directive-*` カスタム要素へマップするものの許可リスト。
// (markdownComponents の "directive-xxx" キーと 1:1 で対応)
//
// これ以外の name は textDirective の誤検出。本文中の `IP:ポート`
// `70:20:10` `20:00` のような「半角コロン + 文字」を remark-directive が
// `:name[...]` インラインディレクティブとパースし、name に CJK / 数字が
// 混ざった不正なタグ名 (`directive-ポートに...`) を作る。これが React の
// SSR で "Invalid tag" 例外となりページ全体が 500 に落ちていた。
// 許可リスト外は素のテキスト (`:name`) に復元してクラッシュを防ぐ。
const ALLOWED_DIRECTIVE_NAMES = new Set([
  "bad",
  "good",
  "faq",
  "faqitem",
  "challenge",
  "point",
  "details",
  "sensei",
  "doubt",
  "answer",
  "spirit",
  "hare",
  "tortoise",
  "error",
  "references",
  "quote",
  "runnable",
  "chart",
  "flowchart",
  "roadmap",
  "experience",
  "figure",
  "video",
  "photo",
  "tweet",
  // 章の終わりのミニテストの置き場 (::miniquiz{chapter="3"})。描き方は使う側が components で渡す
  "miniquiz",
  // 章の見出しの下の絵の置き場 (::chapterhead{chapter="3"})。描き方は使う側が components で渡す
  "chapterhead",
]);

// textDirective ノードを「素の `:name...` テキスト」に戻す。
// remark-directive は `:name[label]{attrs}` をノード化し、その際 ":" や
// label / attr の生テキストは消えているため、name と子テキストから
// 元の見た目を復元する。完璧な原文再現でなく「コロン区切りの文字列が
// そのまま表示される」ことを保証するのが目的。
function revertTextDirectiveToText(node: any): void {
  const childText = Array.isArray(node.children)
    ? node.children
        .map((c: any) => (c && typeof c.value === "string" ? c.value : ""))
        .join("")
    : "";
  node.type = "text";
  node.value = `:${node.name ?? ""}${childText}`;
  delete node.children;
  delete node.attributes;
  delete node.name;
  if (node.data) {
    delete node.data.hName;
    delete node.data.hProperties;
  }
}

function remarkCustomDirectives(options?: {
  source?: string;
  runnableSources?: RunnableSource[];
}) {
  // 優先順位: runnableSources (外で memo 化したもの) > source からその場で抽出
  const runnableSources =
    options?.runnableSources ?? extractRunnableSources(options?.source ?? "");
  let runnableIndex = 0;
  return (tree: any) => {
    visit(tree, (node: any) => {
      if (
        node.type === "containerDirective" ||
        node.type === "leafDirective" ||
        node.type === "textDirective"
      ) {
        // 許可リスト外のディレクティブは誤検出。素のテキストに戻して
        // 不正タグによる SSR クラッシュ (500) を防ぐ。
        // container / leaf (`:::name` / `::name`) は意図的記法のみ通る
        // 想定なので、誤検出が起きやすい inline textDirective を主対象に
        // 復元する。container/leaf も許可外なら同様に無害化する。
        if (!ALLOWED_DIRECTIVE_NAMES.has(node.name)) {
          if (node.type === "textDirective") {
            revertTextDirectiveToText(node);
          } else {
            // container / leaf の許可外: directive-* 要素にせず div として描画
            const data = node.data || (node.data = {});
            data.hName = "div";
            data.hProperties = { ...node.attributes };
          }
          return;
        }

        const data = node.data || (node.data = {});
        const tagName = `directive-${node.name}`;

        data.hName = tagName;

        // labelを取得（node.labelまたは最初の子テキストから）
        let label = node.label;

        // container directiveの場合、[label]は最初の子paragraphになる。
        // remark-directive はその段落に data.directiveLabel を立てるので、
        // 中身が何語でもそこを見れば分かる。
        //
        // **文字の見た目で判定しない。** 以前は最初のテキストが
        // /^[a-z0-9-]+$/ に一致するときだけラベルとして拾っていた。
        // 日本語の見出しは一致しないので label が付かず、
        // :::point[組み上がった文には印が残らない] は既定の
        // 「この章のポイント」を出したうえ、書いた見出しが本文の
        // 1行目として流れていた。著者が一番効かせたかった一言が、
        // 他の段落と同じ見た目で埋もれる
        if (
          !label &&
          node.type === "containerDirective" &&
          node.children?.length > 0
        ) {
          const firstChild = node.children[0];
          const isLabelParagraph =
            firstChild?.type === "paragraph" &&
            (firstChild.data as { directiveLabel?: boolean } | undefined)
              ?.directiveLabel === true;
          if (isLabelParagraph && firstChild.children?.length > 0) {
            // ラベルの中に `コード` などが混ざっていても、字だけ拾う
            const text = firstChild.children
              .map((child: { type: string; value?: string }) =>
                typeof child.value === "string" ? child.value : "",
              )
              .join("")
              .trim();
            if (text) {
              label = text;
              node.children.shift();
            }
          }
        }

        // :::runnable の場合、元ソースから直接コード本文を切り出してインデントを保持する
        let runnableResolvedFromSource = false;
        if (node.name === "runnable" && node.children?.length > 0) {
          const srcBlock = runnableSources[runnableIndex++];
          if (srcBlock != null) {
            // paragraphで包むとインデントが失われるので、code (コードブロック) として保持する
            // rehypeRaw で <pre><code> になるが、directive-runnable ハンドラ側で extractText が拾う
            node.children = [
              {
                type: "code",
                value: srcBlock.code,
                lang: srcBlock.language ?? null,
                meta: null,
              },
            ];
            // データ属性にも格納してレンダリング側で確実に取れるようにする
            (node.attributes ??= {}).code = srcBlock.code;
            if (srcBlock.language) {
              node.attributes.language = srcBlock.language;
            }
            runnableResolvedFromSource = true;
          }
        }
        if (!runnableResolvedFromSource && node.name === "runnable" && node.children?.length > 0) {
          // paragraph内のインライン要素（link/inlineCode/emphasis等）からも原文テキストを復元する
          const extractInlineText = (nodes: any[]): string => {
            if (!nodes) return "";
            return nodes
              .map((c: any) => {
                if (!c) return "";
                if (c.type === "text") return c.value ?? "";
                // URLが自動リンク化された場合: link.url を復元
                if (c.type === "link") {
                  const inner = extractInlineText(c.children || []);
                  // テキストとURLが同じ（自動リンク）ならURLを返す
                  return inner || c.url || "";
                }
                if (c.type === "inlineCode") return c.value ?? "";
                if (c.type === "emphasis" || c.type === "strong" || c.type === "delete") {
                  return extractInlineText(c.children || []);
                }
                if (c.type === "break") return "\n";
                if (c.children) return extractInlineText(c.children);
                return c.value ?? "";
              })
              .join("");
          };

          const extractCodeFromParagraphs = (children: any[]): string => {
            const lines: string[] = [];
            for (const child of children) {
              if (child.type === "paragraph" && child.children) {
                lines.push(extractInlineText(child.children));
              } else if (child.type === "code") {
                lines.push(child.value);
              } else if (child.type === "text") {
                lines.push(child.value);
              }
            }
            return lines.join("\n");
          };

          const codeContent = extractCodeFromParagraphs(node.children);
          // コンテンツを単一のテキストノードに置き換え
          node.children = [
            {
              type: "text",
              value: codeContent,
            },
          ];
        }

        data.hProperties = {
          ...node.attributes,
          ...(label ? { label } : {}),
        };
      }
    });
  };
}

// 悪い指示コンポーネント
const BadExampleBlock = memo(function BadExampleBlock({ children, kind }: { children?: React.ReactNode; kind?: ExampleKind }) {
  return (
    <div className="my-6 flex flex-col rounded-lg border border-main bg-surface p-6 pb-2">
      <div className="flex items-center gap-1">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C13.3132 2.00001 14.6139 2.2582 15.8271 2.76074C17.0403 3.26329 18.1427 4.00016 19.0713 4.92871C19.9998 5.85718 20.7357 6.95978 21.2383 8.17285C21.7408 9.38611 22 10.6868 22 12C22 13.9778 21.4133 15.9112 20.3145 17.5557C19.2157 19.2001 17.6543 20.4824 15.8271 21.2393C13.9999 21.9961 11.9886 22.1935 10.0488 21.8076C8.10913 21.4217 6.32717 20.4697 4.92871 19.0713C3.53026 17.6728 2.57828 15.8909 2.19238 13.9512C1.80653 12.0114 2.00387 10.0001 2.76074 8.17285C3.51762 6.34566 4.79991 4.78433 6.44434 3.68555C8.08883 2.58673 10.0222 2 12 2ZM15.0615 4.60938C13.5998 4.00391 11.9912 3.84472 10.4395 4.15332C8.88762 4.462 7.46159 5.22397 6.34277 6.34277C5.22395 7.46159 4.462 8.88761 4.15332 10.4395C3.8447 11.9912 4.0039 13.5998 4.60938 15.0615C5.21489 16.5232 6.24013 17.7724 7.55566 18.6514C8.87126 19.5304 10.4178 20 12 20C14.1217 20 16.1569 19.1575 17.6572 17.6572C19.1575 16.1569 20 14.1217 20 12C20 10.4177 19.5304 8.87126 18.6514 7.55566C17.7724 6.24014 16.5232 5.21489 15.0615 4.60938ZM12.1963 15.0195C12.3903 15.0569 12.5693 15.1513 12.71 15.29L12.8301 15.4404C12.8678 15.4961 12.8981 15.5565 12.9199 15.6201C12.9557 15.6756 12.9828 15.7361 13 15.7998C13.0049 15.8664 13.0049 15.9334 13 16C12.9984 16.1306 12.9711 16.2597 12.9199 16.3799C12.875 16.504 12.8033 16.6166 12.71 16.71C12.6166 16.8033 12.504 16.875 12.3799 16.9199C12.2602 16.9728 12.1308 17 12 17C11.8692 17 11.7398 16.9728 11.6201 16.9199C11.496 16.875 11.3834 16.8033 11.29 16.71C11.1967 16.6166 11.125 16.504 11.0801 16.3799C11.0289 16.2597 11.0016 16.1307 11 16C10.9992 15.8684 11.0245 15.738 11.0742 15.6162C11.124 15.4944 11.1974 15.3835 11.29 15.29C11.3872 15.2017 11.4989 15.1307 11.6201 15.0801C11.802 15.0036 12.0025 14.9823 12.1963 15.0195ZM12 7C12.2652 7.00001 12.5195 7.10544 12.707 7.29297C12.8946 7.4805 13 7.73479 13 8V12C13 12.2652 12.8946 12.5195 12.707 12.707C12.5195 12.8946 12.2652 13 12 13C11.7348 13 11.4805 12.8946 11.293 12.707C11.1054 12.5195 11 12.2652 11 12V8C11 7.73478 11.1054 7.4805 11.293 7.29297C11.4805 7.10545 11.7348 7 12 7Z"
            fill="#F47365"
          />
        </svg>

        <span className="text-sm font-bold" style={{ color: "var(--lg-danger, #F47365)" }}>
          {EXAMPLE_LABELS[kind ?? "instruction"].bad}
        </span>
      </div>
      <div className="text-sm text-main leading-[1.8] [&>h1]:text-base [&>h1]:mt-4 [&>h1]:mb-2 [&>h2]:text-sm [&>h2]:mt-4 [&>h2]:mb-2 [&>h3]:text-sm [&>h3]:mt-3 [&>h3]:mb-1">{children}</div>
    </div>
  );
});

// 良い指示コンポーネント
const GoodExampleBlock = memo(function GoodExampleBlock({ children, kind }: { children?: React.ReactNode; kind?: ExampleKind }) {
  return (
    <div className="my-6 flex flex-col rounded-lg border border-main bg-surface p-6 pb-2">
      <div className="flex items-center gap-1">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C13.3132 2.00001 14.6139 2.2582 15.8271 2.76074C17.0403 3.26329 18.1427 4.00016 19.0713 4.92871C19.9998 5.85718 20.7357 6.95978 21.2383 8.17285C21.7408 9.38611 22 10.6868 22 12C22 13.9778 21.4133 15.9112 20.3145 17.5557C19.2157 19.2001 17.6543 20.4824 15.8271 21.2393C13.9999 21.9961 11.9886 22.1935 10.0488 21.8076C8.10913 21.4217 6.32717 20.4697 4.92871 19.0713C3.53026 17.6728 2.57828 15.8909 2.19238 13.9512C1.80653 12.0114 2.00387 10.0001 2.76074 8.17285C3.51762 6.34566 4.79991 4.78433 6.44434 3.68555C8.08883 2.58673 10.0222 2 12 2ZM15.0615 4.60938C13.5998 4.00391 11.9912 3.84472 10.4395 4.15332C8.88762 4.462 7.46159 5.22397 6.34277 6.34277C5.22395 7.46159 4.462 8.88761 4.15332 10.4395C3.8447 11.9912 4.0039 13.5998 4.60938 15.0615C5.21489 16.5232 6.24013 17.7724 7.55566 18.6514C8.87126 19.5304 10.4178 20 12 20C14.1217 20 16.1569 19.1575 17.6572 17.6572C19.1575 16.1569 20 14.1217 20 12C20 10.4177 19.5304 8.87126 18.6514 7.55566C17.7724 6.24014 16.5232 5.21489 15.0615 4.60938ZM15.4248 8.49902C15.689 8.49902 15.9425 8.6038 16.1299 8.79004C16.2236 8.88294 16.2979 8.99347 16.3486 9.11523C16.3994 9.23709 16.4258 9.36799 16.4258 9.5C16.4258 9.63201 16.3994 9.76291 16.3486 9.88477C16.2979 10.0065 16.2236 10.1171 16.1299 10.21L11.1299 15.21C10.9436 15.3947 10.692 15.4989 10.4297 15.5C10.2982 15.5007 10.1677 15.4755 10.0459 15.4258C9.92411 15.376 9.81314 15.3026 9.71973 15.21L7.37012 12.8496C7.26556 12.76 7.18011 12.6503 7.12012 12.5264C7.06009 12.4023 7.0268 12.2666 7.02148 12.1289C7.01619 11.9913 7.03863 11.8538 7.08887 11.7256C7.13912 11.5973 7.21609 11.4812 7.31348 11.3838C7.41087 11.2864 7.52705 11.2095 7.65527 11.1592C7.78347 11.109 7.92102 11.0856 8.05859 11.0908C8.19631 11.0961 8.33199 11.1304 8.45605 11.1904C8.58005 11.2504 8.69067 11.3358 8.78027 11.4404L10.4297 13.0898L14.7197 8.79004C14.9071 8.60382 15.1607 8.49906 15.4248 8.49902Z"
            fill="#05B45B"
          />
        </svg>

        <span className="text-sm font-bold" style={{ color: "var(--lg-done-text, #05B45B)" }}>
          {EXAMPLE_LABELS[kind ?? "instruction"].good}
        </span>
      </div>
      <div className="text-sm text-main leading-[1.8] [&>h1]:text-base [&>h1]:mt-4 [&>h1]:mb-2 [&>h2]:text-sm [&>h2]:mt-4 [&>h2]:mb-2 [&>h3]:text-sm [&>h3]:mt-3 [&>h3]:mb-1">{children}</div>
    </div>
  );
});

/**
 * 言語の無いコードの塊が、記号や空白で形を作った図かどうか。次のどれかがあれば図とみなし、折り返さずに横へ送る。
 * 1 罫線の字 (U+2500 から U+257F)、「+--」「--+」の角、行の両端の「|」
 * 2 同じ行に矢印が 2 つ以上ある (U+2190 から U+21FF の字、「->」「<-」「=>」)。1 行に 1 つだけの対応表は文の塊のまま
 * 3 空白でない字の間に 3 つ以上続く空白がある行が 2 行以上 (空白で列を揃えた図)
 * 4 「|」を含む行が 2 行以上 (片側だけ線を引く筆算の図)
 * 5 「[ ]」で囲んだ箱が同じ行に 2 つ以上
 * 1 行 1 項目で空白が 1 つか 2 つの対応表や文章は、どれにも当たらず文の塊として折り返す
 */
export function isTextArtBlock(code: string): boolean {
  if (/[\u2500-\u257F]/.test(code)) return true;
  if (/\+-{2,}|-{2,}\+/.test(code)) return true;
  if (/^\s*\|.*\|\s*$/m.test(code)) return true;
  const lines = code.split("\n");
  const arrow = /[\u2190-\u21FF]|->|<-|=>/g;
  if (lines.some((line) => (line.match(arrow) ?? []).length >= 2)) return true;
  if (lines.filter((line) => /\S {3,}\S/.test(line)).length >= 2) return true;
  if (lines.filter((line) => line.includes("|")).length >= 2) return true;
  return lines.some((line) => /\[[^\]]+\][^[]*\[[^\]]+\]/.test(line));
}

// FAQコンポーネント（複数のfaqitemを含む）
const FaqBlock = memo(function FaqBlock({ children }: { children?: React.ReactNode }) {
  return <div className="my-6 flex flex-col gap-4">{children}</div>;
});

// FAQ 項目コンポーネント（q属性で質問、中身が回答）
const FaqItem = memo(function FaqItem({
  children,
  q,
  renderQuestion,
}: {
  children?: React.ReactNode;
  q?: string;
  renderQuestion?: (q: string) => React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-lg border border-main bg-surface p-6">
      {q && (
        <div className="flex items-start gap-2">
          <HelpCircle
            className="w-5 h-5 shrink-0 mt-0.5"
            style={{ color: "var(--lg-c-plan-ink, #1D6FD3)" }}
          />
          <span className="text-sm font-bold" style={{ color: "var(--lg-c-plan-ink, #1D6FD3)" }}>
            {renderQuestion ? renderQuestion(q) : q}
          </span>
        </div>
      )}
      <div className="text-sm text-main leading-[1.8] pl-7 mt-2 [&>p]:!m-0 [&>p]:!mb-2 [&>p:last-child]:!mb-0">
        {children}
      </div>
    </div>
  );
});

/*
# 本文に浮かぶ囲みの地について

ここから下の囲み (良い例・悪い例・ポイント・FAQ・チャレンジ・エラー・引用) は
すべて `bg-second` を着ていた。**ダークでは地 (primary-950) と bg-second
(primary-900) が両方 #012e1a で、囲みが丸ごと消える。** 中の電球と字だけが
宙に浮いて見えていた (apps/client/CLAUDE.md に同じ注意がある)。

`bg-surface` はライトで #ffffff、ダークで primary-800。どちらでも地より
一段手前に出る。ライトでは生成りの地の上で白が浮くだけになるので、
枠線も足して輪郭を残している。

AI先生の吹き出しだけは `bg-accent-light` だった。これは**ライトとダークで
同じ accent-50** なので、ダークで白文字が乗ると読めない。もぐらの吹き出しと
同じ bg-surface にして、accent は枠と「AI先生」の名前が持つ。
*/
// 幅が和文 10 字分以下の短い表のセルは折らない (折れると 1〜2 字だけの行ができる)。英数字は和文の約半分の幅で数える (「PRIMARY KEY」「db/seed.sql」も折らない)
const SHORT_CELL_MAX = 10;
function cellWidth(text: string): number {
  let width = 0;
  for (const ch of text) width += ch.charCodeAt(0) < 0x100 ? 0.55 : 1;
  return width;
}
/**
 * 1 行に収めたい短いインラインコード (`git add`、`--staged` など) の大きさ。空白の所で「git / add」と割れると命令が読めないので、
 * 描く側は data-short-code に入れ、見せる側 (CSS) が折らない。幅は cellWidth と同じ数え方で、s は 6.6 まで (英数字 12 字。表の狭い列でも折らない)、
 * m は 12 まで (英数字 21 字ほど。本文の 1 行には収まる)。それより長い物は undefined
 */
export function shortInlineCode(text: string): "s" | "m" | undefined {
  const t = text.trim();
  if (!t) return undefined;
  const w = cellWidth(t);
  return w <= 6.6 ? "s" : w <= 12 ? "m" : undefined;
}
/**
 * 空白の無い長いインラインコード (パスや URL) に、折ってよい所 (区切りの / と \ の後、. の前) の印 (<wbr>) を入れる。
 * 印が無いと、収まらないときに語の途中 (「memo.t / xt」) で折れる。写したときの字は変わらない
 */
function withPathBreaks(text: string): React.ReactNode {
  if (/\s/.test(text)) return text;
  const parts = text.split(/(?<=[/\\])(?![/\\])|(?=\.[A-Za-z])/);
  // 頭の / (と ~/ や C:\) だけの切れ端は次とつなぐ。行の終わりに「/」だけが残らないように
  if (parts.length > 1 && parts[0].length <= 3) parts.splice(0, 2, parts[0] + parts[1]);
  if (parts.length < 2) return text;
  return parts.map((part, i) => (i === 0 ? part : <Fragment key={i}><wbr />{part}</Fragment>));
}
function cellText(node: React.ReactNode): string | null {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) {
    let out = "";
    for (const n of node) {
      const t = cellText(n);
      if (t === null) return null;
      out += t;
    }
    return out;
  }
  if (isValidElement(node)) return cellText((node.props as { children?: React.ReactNode }).children);
  return null;
}
function isShortCell(children: React.ReactNode): boolean {
  const t = cellText(children);
  return t !== null && t.trim().length > 0 && cellWidth(t.trim()) <= SHORT_CELL_MAX;
}
/**
 * 短いセルの印の値。空白を含むセル (「有期性 (ゆうきせい)」) は "space" にし、見せる側が狭い画面で折るときに空白の所でだけ折れるようにする
 * (文節で折ると読み仮名の途中「ゆう / きせい」で割れた)。空白の無いセルは ""
 */
function shortCellMark(children: React.ReactNode): string | undefined {
  if (!isShortCell(children)) return undefined;
  return /\s/.test((cellText(children) ?? "").trim()) ? "space" : "";
}

// この章/記事のポイントブロック
const PointBlock = memo(function PointBlock({
  children,
  label,
}: {
  children?: React.ReactNode;
  label?: string;
}) {
  return (
    <div className="my-6 flex flex-col rounded-lg border border-main bg-surface p-6 pb-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Lightbulb className="w-5 h-5" style={{ color: "#F59E0B" }} />
        <span className="text-sm font-bold text-main">
          {label || "この章のポイント"}
        </span>
      </div>
      <div className="text-sm text-main leading-[1.8] [&>ul]:my-0 [&>p:last-child]:mb-0">
        {children}
      </div>
    </div>
  );
});

// 畳んでおく解説。:::details[見出し] と書くと、見出しだけを出して既定では閉じておく。
// 手順を先に読ませたい所 (クラウドエディターの手順の板など) で、文法の説明のような
// 「要る人だけ広げる」ものを本文から外すために使う。開け閉めはブラウザの <details> に任せる
const DetailsBlock = memo(function DetailsBlock({
  children,
  label,
}: {
  children?: React.ReactNode;
  label?: string;
}) {
  return (
    <details className="group my-4 rounded-lg border border-main bg-surface">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-main [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        {label || "くわしく"}
      </summary>
      <div className="px-4 pb-3 text-sm text-main leading-[1.8] [&>p:last-child]:mb-0">
        {children}
      </div>
    </details>
  );
});

// AI先生の一言。FigureDeck の SenseiNote と同じ顔を使い、
// 本文の補足だけを「誰が話しているか」分かる形で挿入する。
const SenseiBlock = memo(function SenseiBlock({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <aside className="my-8 flex items-start gap-3" data-sensei-note>
      <div className="mt-2 shrink-0" aria-hidden="true">
        <OrangeSenseiIcon variant="thinking" size={36} />
      </div>
      <div className="relative min-w-0 flex-1 rounded-2xl border border-accent bg-surface px-5 py-4">
        <span
          aria-hidden="true"
          className="absolute left-[-7px] top-5 h-3.5 w-3.5 rotate-45 border-b border-l border-accent bg-surface"
        />
        <p className="mb-1 text-sm font-bold text-accent">AI先生</p>
        <div className="text-base leading-[1.9] text-main [text-wrap:wrap] [word-break:auto-phrase] [&>p]:my-0 [&>p:not(:last-child)]:mb-2">
          {children}
        </div>
      </div>
    </aside>
  );
});

// もぐらの「でも、〜なんじゃない？」。
//
// **もぐらは答えを言わない。** 役は読者の中の声で、多くの人がここで思う
// もっともらしい誤解を1文だけ言う。答えは直後の本文か箇条書きか図が出す。
// 問いと答えを同じ顔にすると、どの台詞を信じてよいか読者が判定できないので、
// 説明する側は AI先生 (:::sensei) に分けてある。
//
// 絵は動かない。読んでいる横で動くものがあると、そちらへ目が行って文章が
// 頭に入らない。動くのは復習ミニクイズで正誤が出た瞬間だけ。
const DoubtBlock = memo(function DoubtBlock({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <aside className="my-8 flex items-start gap-3" data-doubt-note>
      {/* 絵は消費側アプリの public に置く。原画を shared-ui へ持ち込むと
          パス data が2か所になり、片方だけ描き直したときに顔が変わる */}
      <img
        src="/mascot/mogura-01.svg"
        alt=""
        aria-hidden="true"
        width={48}
        height={50}
        className="mt-1 shrink-0"
      />
      {/* 地が生成りなので bg-second だと輪郭が枠線だけになる。
          bg-surface はライトで白、ダークで一段明るい面になり、どちらでも
          地より手前に出る */}
      <div className="relative min-w-0 flex-1 rounded-2xl border border-main bg-surface px-5 py-4">
        <span
          aria-hidden="true"
          className="absolute left-[-7px] top-5 h-3.5 w-3.5 rotate-45 border-b border-l border-main bg-surface"
        />
        <div className="text-base leading-[1.9] text-main [text-wrap:wrap] [word-break:auto-phrase] [&>p]:my-0 [&>p:not(:last-child)]:mb-2">
          {children}
        </div>
      </div>
    </aside>
  );
});

// うさぎの「もっと速く行けるはずでしょ？」。**/lab の記事だけで使う。**
//
// レッスンのもぐらは教材への誤解を言うが、記事には誤解する教材が無い。
// 記事で読者が抱えているのは焦りで、感情が別なので顔も分ける。
// もぐらに兼務させると、/lab に既に居る読者側の声 ゆめこ (【ゆ】) とも役が重なる。
//
// **疑う先は世間の思い込みか読者自身に限る。** 記事には実名の人が出てくるので、
// その人を疑うと読者に不快に映る。実際、最初に書いた台詞は取材相手を
// 疑う形になっていて書き直した。
// 地の色はキャラの色に寄せる。白い吹き出しのままだと、取材相手の色付きの
// 吹き出しに挟まれて、キャラの言葉が地の文に見える
const HareBlock = memo(function HareBlock({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <aside className="my-8 flex items-start gap-3" data-hare-note>
      <img
        src="/mascot/usagi-hirameki-02.svg"
        alt=""
        aria-hidden="true"
        width={36}
        height={42}
        className="mt-1 shrink-0"
      />
      <div className="relative min-w-0 flex-1 rounded-2xl border border-[#f0d6b3] bg-[#fff6ea] dark:border-[#6b5436] dark:bg-[#3a2e1f] px-5 py-4">
        <span
          aria-hidden="true"
          className="absolute left-[-7px] top-5 h-3.5 w-3.5 rotate-45 border-b border-l border-[#f0d6b3] bg-[#fff6ea] dark:border-[#6b5436] dark:bg-[#3a2e1f]"
        />
        <div className="text-base leading-[1.9] text-main [text-wrap:wrap] [word-break:auto-phrase] [&>p]:my-0 [&>p:not(:last-child)]:mb-2">
          {children}
        </div>
      </div>
    </aside>
  );
});

// AI精霊の「それ本当？」。記事の側で使う問いかけ役で、受けるのは
// ふくろうの :::answer。もぐらと役は同じだが、もぐらはレッスンの中で
// 学習者の分身として置いてある。記事は読み手が受講生とは限らないので、
// 同じ絵を出すと「自分のこと」に読めてしまう。
//
// 絵が縦に長い (189x350) ので、もぐらと同じ 48 幅にすると背丈が
// 2 倍近くなって吹き出しの高さを超える。高さのほうを 50 に合わせる。
const SpiritBlock = memo(function SpiritBlock({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <aside className="my-8 flex items-start gap-3" data-spirit-note>
      <img
        src="/mascot/seirei-01.svg"
        alt=""
        aria-hidden="true"
        width={27}
        height={50}
        className="mt-1 shrink-0"
      />
      <div className="relative min-w-0 flex-1 rounded-2xl border border-main bg-surface px-5 py-4">
        <span
          aria-hidden="true"
          className="absolute left-[-7px] top-5 h-3.5 w-3.5 rotate-45 border-b border-l border-main bg-surface"
        />
        <div className="text-base leading-[1.9] text-main [text-wrap:wrap] [word-break:auto-phrase] [&>p]:my-0 [&>p:not(:last-child)]:mb-2">
          {children}
        </div>
      </div>
    </aside>
  );
});

// かめの「そこはこう」。うさぎの焦りを受ける側。
//
// **ふくろうと口調を分ける。** ふくろうは老賢者で 〜じゃ を使うが、かめは素で話す。
// 同じ一族の絵に見えたまま、レッスンの回と記事の回が混ざらない。
//
// **相手を評価しない。** 「焦らなくていい」「自分のペースで」は標語になって
// 何も足さないので言わせない。代わりに、その記事だけで言える具体を足す。
// 「5〜6時間は休職中だから置けた数」のように、読者が自分の場合へ引き算できる形にする。
const TortoiseBlock = memo(function TortoiseBlock({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <aside className="my-8 flex items-start justify-end gap-3" data-tortoise-note>
      <div className="relative min-w-0 flex-1 rounded-2xl border border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-900 px-5 py-4">
        <span
          aria-hidden="true"
          className="absolute right-[-7px] top-5 h-3.5 w-3.5 rotate-45 border-r border-t border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-900"
        />
        <div className="text-base leading-[1.9] text-main [text-wrap:wrap] [word-break:auto-phrase] [&>p]:my-0 [&>p:not(:last-child)]:mb-2">
          {children}
        </div>
      </div>
      <img
        src="/mascot/kame-yochi-clean.svg"
        alt=""
        aria-hidden="true"
        width={56}
        height={37}
        className="mt-3 shrink-0"
      />
    </aside>
  );
});

// チャレンジ/ヒントブロック
const ChallengeBlock = memo(function ChallengeBlock({ children }: { children?: React.ReactNode }) {
  return (
    <div className="my-6 flex flex-col rounded-lg border border-main bg-surface p-6 pb-2">
      <div className="flex items-center gap-1">
        <Lightbulb className="w-5 h-5" style={{ color: "#F59E0B" }} />
        <span className="text-sm font-bold text-main">
          チャレンジ
        </span>
      </div>
      <div className="text-sm text-main leading-[1.8]">{children}</div>
    </div>
  );
});

// エラー解決ブロック
const ErrorTipBlock = memo(function ErrorTipBlock({ children }: { children?: React.ReactNode }) {
  return (
    <div className="my-6 flex flex-col rounded-lg border border-main bg-surface p-6 pb-2">
      <div className="flex items-center gap-1">
        <AlertTriangle className="w-5 h-5" style={{ color: "#F47365" }} />
        <span className="text-sm font-bold text-main">
          よくあるエラーと解決法
        </span>
      </div>
      <div className="text-sm text-main leading-[1.8]">{children}</div>
    </div>
  );
});

// 参考リンクブロック - 子要素からURLを抽出してOGPLinkCardで表示
const ReferencesBlock = memo(function ReferencesBlock({ children }: { children?: React.ReactNode }) {
  // childrenからURLを抽出
  const extractUrls = (node: React.ReactNode): string[] => {
    const urls: string[] = [];

    const traverse = (n: any): void => {
      if (!n) return;

      // 文字列の場合、URLパターンを探す
      if (typeof n === "string") {
        const urlMatch = n.match(/https?:\/\/[^\s]+/g);
        if (urlMatch) {
          urls.push(...urlMatch);
        }
        return;
      }

      // 配列の場合
      if (Array.isArray(n)) {
        n.forEach(traverse);
        return;
      }

      // React要素の場合
      if (n && typeof n === "object") {
        // aタグの場合、hrefを取得
        if (n.props?.href) {
          urls.push(n.props.href);
          return;
        }
        // 子要素を再帰的に探索
        if (n.props?.children) {
          traverse(n.props.children);
        }
      }
    };

    traverse(node);
    return [...new Set(urls)]; // 重複を除去
  };

  const urls = extractUrls(children);

  return (
    <div className="my-6 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.16699 4.16797C9.62708 4.16814 10 4.54085 10 5.00098C10 5.46111 9.62708 5.83381 9.16699 5.83398H4.16699C3.94598 5.83398 3.73343 5.92184 3.57715 6.07812C3.42087 6.23441 3.33301 6.44695 3.33301 6.66797V15.834C3.33301 16.055 3.42087 16.2675 3.57715 16.4238C3.73343 16.5801 3.94598 16.668 4.16699 16.668H13.333C13.554 16.668 13.7666 16.5801 13.9229 16.4238C14.0791 16.2675 14.167 16.055 14.167 15.834V10.834C14.1672 10.3739 14.5399 10.001 15 10.001C15.4601 10.001 15.8328 10.3739 15.833 10.834V15.834C15.833 16.497 15.5694 17.1327 15.1006 17.6016C14.6317 18.0704 13.996 18.334 13.333 18.334H4.16699C3.50395 18.334 2.86825 18.0704 2.39941 17.6016C1.93057 17.1327 1.66699 16.497 1.66699 15.834V6.66797C1.66699 6.00493 1.93057 5.36923 2.39941 4.90039C2.86826 4.43155 3.50395 4.16797 4.16699 4.16797H9.16699ZM17.5 1.66797C17.5419 1.66797 17.5828 1.67178 17.623 1.67773C17.637 1.67981 17.6512 1.68081 17.665 1.68359C17.6796 1.68651 17.6938 1.69067 17.708 1.69434C17.7184 1.697 17.729 1.69907 17.7393 1.70215C17.7525 1.70611 17.7654 1.71122 17.7783 1.71582C17.8155 1.72898 17.8523 1.74409 17.8877 1.7627L17.9658 1.80957C17.9753 1.81595 17.984 1.82333 17.9932 1.83008C18.0266 1.85469 18.0586 1.88188 18.0889 1.91211C18.1188 1.94201 18.1455 1.97384 18.1699 2.00684C18.1782 2.0181 18.1866 2.02933 18.1943 2.04102C18.2322 2.09808 18.2618 2.15883 18.2842 2.22168C18.2888 2.23465 18.2939 2.24748 18.2979 2.26074C18.3093 2.29886 18.3164 2.33784 18.3223 2.37695C18.3283 2.41751 18.333 2.45873 18.333 2.50098V7.50098C18.333 7.96121 17.9602 8.33398 17.5 8.33398C17.0398 8.33398 16.667 7.96121 16.667 7.50098V4.51172L8.92285 12.2568C8.59742 12.5823 8.06958 12.5823 7.74414 12.2568C7.4187 11.9314 7.4187 11.4036 7.74414 11.0781L15.4893 3.33398H12.5C12.0398 3.33398 11.667 2.96121 11.667 2.50098C11.667 2.04074 12.0398 1.66797 12.5 1.66797H17.5Z"
            fill="#05B45B"
          />
        </svg>
        <span className="text-xl font-bold text-main">参考リンク</span>
      </div>
      <div className="flex flex-col gap-3">
        {urls.length > 0
          ? urls.map((url, index) => (
              <OGPLinkCard key={`${url}-${index}`} url={url} />
            ))
          : children}
      </div>
    </div>
  );
});

// 執筆者コメントブロック
// :::quote[contributor-slug]
// コメント内容
// :::
const QuoteBlock = memo(function QuoteBlock({
  children,
  author,
  avatar,
  title,
  label,
}: {
  children?: React.ReactNode;
  author?: string;
  avatar?: string;
  title?: string;
  label?: string;
}) {
  // labelと同じテキストをchildrenからフィルタリング（再帰的に全ての子要素をチェック）
  const filterLabel = (node: React.ReactNode): React.ReactNode => {
    if (!label) return node;

    // 文字列の場合
    if (typeof node === "string") {
      // labelと完全一致または trimして一致する場合は除去
      if (node === label || node.trim() === label) {
        return null;
      }
      return node;
    }

    // 配列の場合
    if (Array.isArray(node)) {
      const filtered = node
        .map(filterLabel)
        .filter((n) => n !== null && n !== undefined);
      return filtered.length > 0 ? filtered : null;
    }

    // React要素の場合
    if (node && typeof node === "object" && "props" in node) {
      const element = node as React.ReactElement;
      const childContent = element.props?.children;

      // 子がlabelと一致する場合は要素ごと除去
      if (
        childContent === label ||
        (typeof childContent === "string" && childContent.trim() === label)
      ) {
        return null;
      }

      // 子要素がある場合は再帰的にフィルタリング
      if (childContent) {
        const filteredChildren = filterLabel(childContent);
        if (
          filteredChildren === null ||
          (Array.isArray(filteredChildren) && filteredChildren.length === 0)
        ) {
          return null;
        }
      }
    }

    return node;
  };

  const filteredChildren = filterLabel(children);
  const hasAuthor = Boolean(author);

  return (
    <div className="my-6 rounded-2xl border border-main bg-surface p-5 pb-2">
      <div className="flex items-start gap-4">
        {/* アバター（contributor が登録されている場合のみ表示） */}
        {avatar && (
          <img
            src={avatar}
            alt={author || ""}
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          {/* 著者情報（author が見つかった場合のみ表示） */}
          {hasAuthor && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-bold text-main">{author}</span>
              {title && <span className="text-xs text-sub">（{title}）</span>}
            </div>
          )}
          {/* コメント内容。著者の行が無い引用では、段落の上の余白 (my-6) が枠の内側の余白に足されて、札 1 行ぶん空いて見えた。最初と最後の段落の外の余白は枠の余白にそろえる */}
          <div className="text-sm text-main leading-[1.8] [&>p:first-child]:!mt-0 [&>p:last-child]:!mb-3">
            {filteredChildren}
          </div>
        </div>
      </div>
    </div>
  );
});

// `::figure[array-index]` のように id を children に持つ形から id を取り出す
/**
 * 表の入れ物。横に送れるとき (右に隠れた部分があるとき) だけ、右の端に地の色の帯を出し、送り切ったら消す。
 * 帯は lg の外では見えない (lg-prose.css が出す)。スマホで表が横に送れることは、帯が右端にあることで分かる
 */
function ScrollableTable({ children, layout, ...props }: React.ComponentProps<"table"> & { layout?: TableLayout }) {
  const [scrollerRef, more] = useScrollMore<HTMLDivElement>();
  return (
    <div className="md-table" data-more={more ? "true" : undefined} data-table-layout={layout}>
      <div ref={scrollerRef} className="overflow-x-auto my-6 md-table__scroll">
        <table className="w-full border-collapse" {...props}>
          {children}
        </table>
      </div>
      <span className="md-table__fade" aria-hidden="true" />
    </div>
  );
}

function figureIdFromChildren(node: any): string {
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(figureIdFromChildren).join("");
  if (node?.props?.children) return figureIdFromChildren(node.props.children);
  return "";
}

// コードから実行可能ブロック情報を抽出するヘルパー
function extractRunnableCode(
  children: React.ReactNode
): { code: string; language: string } | null {
  if (!children) return null;

  // childrenからcode文字列を取得
  const extractText = (node: any): string => {
    if (typeof node === "string") return node;
    if (Array.isArray(node)) return node.map(extractText).join("");
    if (node?.props?.children) return extractText(node.props.children);
    return "";
  };

  const code = extractText(children).replace(/\n$/, "");
  return { code, language: "javascript" };
}

/**
 * 対談形式の記事で使う話者。本文の段落が【ゆ】【編】で始まっていたら、
 * その1行を吹き出しにする。記事側は記号を書くだけでよく、
 * 記事ごとに専用の記法を覚えなくていい。
 */
/*
  左右と色を、記事の作りに合わせて組み替えるための4通り。

  **話者だけでは左右を決められない。** ゆめは講師インタビューでは
  聞き手 (右) だが、代表記事では代表を止める側 (左) になる。同じ人でも
  記事の中での役目が違うので、役目ごとに名前を分けてある。

  色は「その記事の主の声」に付ける。インタビューなら答える人、代表記事なら
  代表。左右と色を別々に選べないと、主が左に来たり色が聞き手に付いたりする
*/
type DialogueKind = "host" | "guest" | "challenger" | "author";

/**
 * 対話をチャットの形で出す。
 *
 * # どちらを左に置くか
 *
 * **答える側を左、聞き手を右にしている。** チャットの見慣れた形とは逆だが、
 * 答えは 2〜4 行あり、質問は 1 行で終わる。長いほうを左に固定すると、
 * 読み手が何百回も戻る左端が動かない。短い質問が右に浮いて、往復の
 * 区切りとして働く。逆にすると、いちばん読む文の行頭が発言ごとにずれる。
 *
 * 聞き手が右なのは意味の上でも合っている。田中はゆめさく側の人間で、
 * チャットで右に出るのは画面の持ち主だという読み方ができる。
 *
 * # 同じ話者が続くとき
 *
 * 名前と顔を消して、吹き出しだけを続ける。隣り合わせの CSS でやるので、
 * React 側に「前の話者」を持たせない。段落ごとの純粋な描画に可変の状態を
 * 持ち込むと、StrictMode の二重描画で崩れる。見出しや画像が挟まると
 * 隣り合わせが切れて名前が自分で戻る。
 *
 * # 吹き出しの幅
 *
 * 88% で止める。100% にすると吹き出しに見えず、ただの色つきの帯になる。
 * 狭くしすぎると 4 行が 6 行になって、81 往復ぶんの縦が伸びる。
 */
const DIALOGUE_KINDS: Record<
  DialogueKind,
  { row: string; content: string; head: string; name: string; bubble: string }
> = {
  host: {
    // 質問の前で往復が変わる。ここだけ広くあける
    row: "mt-8 flex flex-row-reverse items-start gap-2.5 [[data-dialogue=host]+&]:mt-1.5",
    // 吹き出しを右端へ寄せる。中の文は左揃えのまま
    content: "min-w-0 flex-1 text-right",
    head: "mb-1 flex flex-row-reverse items-center gap-2 [[data-dialogue=host]+[data-dialogue=host]_&]:hidden",
    name: "text-[15px] font-bold tracking-[0.02em] text-sub",
    bubble:
      "max-w-[88%] rounded-2xl rounded-tr-sm bg-black-50 px-4 py-2.5 text-[15px] leading-[1.85] text-main dark:bg-black-700",
  },
  guest: {
    row: "mt-3 flex items-start gap-2.5 [[data-dialogue=guest]+&]:mt-1.5",
    content: "min-w-0 flex-1",
    head: "mb-1 flex items-center gap-2 [[data-dialogue=guest]+[data-dialogue=guest]_&]:hidden",
    // 12px の小さい文字なので、白地に対して 4.5:1 を切らない濃さにする。
    // ブランドの #ea5504 は 3.62:1 で足りない。ダークは accent-200。
    // accent-300 (#f97c6a) は 4.15:1 で足りない
    name: "text-[15px] font-bold tracking-[0.02em] text-[#c44703] dark:text-accent-200",
    bubble:
      "max-w-[88%] rounded-2xl rounded-tl-sm bg-accent-50 px-4 py-2.5 text-base leading-[1.85] text-main dark:bg-accent-900",
  },
  /* 代表記事で代表を止める側。左に置き、色は付けない */
  challenger: {
    row: "mt-8 flex items-start gap-2.5 [[data-dialogue=challenger]+&]:mt-1.5",
    content: "min-w-0 flex-1",
    head: "mb-1 flex items-center gap-2 [[data-dialogue=challenger]+[data-dialogue=challenger]_&]:hidden",
    name: "text-[15px] font-bold tracking-[0.02em] text-sub",
    bubble:
      "max-w-[88%] rounded-2xl rounded-tl-sm bg-black-50 px-4 py-2.5 text-[15px] leading-[1.85] text-main dark:bg-black-700",
  },
  /*
    代表記事の主の声。右に置き、ブランドの緑にする。聞き手のゆめこ (guest) は
    朱色なので、同じ記事に並んでも色で話者が分かれる
  */
  author: {
    row: "mt-3 flex flex-row-reverse items-start gap-2.5 [[data-dialogue=author]+&]:mt-1.5",
    content: "min-w-0 flex-1 text-right",
    head: "mb-1 flex flex-row-reverse items-center gap-2 [[data-dialogue=author]+[data-dialogue=author]_&]:hidden",
    name: "text-[15px] font-bold tracking-[0.02em] text-primary-700 dark:text-primary-200",
    bubble:
      "max-w-[88%] rounded-2xl rounded-tr-sm bg-primary-50 px-4 py-2.5 text-base leading-[1.85] text-main dark:bg-primary-900",
  },
};

const DIALOGUE_SPEAKERS: Record<
  string,
  { name: string; role?: string; kind: DialogueKind; avatarUrl?: string }
> = {
  // 頭文字を丸に入れたアイコンは CMS の既定アバターに見えて安っぽい。
  // 実物の似顔絵が入るまでは名前だけで分ける。avatarUrl を入れれば丸が出る。
  // 13px の小さい文字なので、白地に対して 4.5:1 を切らない濃さにする。
  // ブランドの #ea5504 は 3.62:1、素のグレー #7a8891 は 3.65:1 で足りない
  // 絵は figure-kit の読者役 (woman-02)。ゆめこは読者の代わりに聞く役なので同じ絵にする
  "ゆ": {
    name: "ゆめこ",
    kind: "guest",
    avatarUrl: "/lab/speaker-yumeko-woman02.webp",
  },
  // 代表の記事 (/lab/founder) の対談形式で使う。ゆめこと同じ色にしてあるのは、
  // どちらも「運営側の声」で、同じ記事に並ぶことがないため
  "代": { name: "ゆめさく(代表)", kind: "guest" },
  "編": { name: "編集部", kind: "host" },
  // 聞き手に顔と名前を出す試作。いまは講師インタビュー1本 (生田講師) だけで
  // 使っている。**「編」を置き換えたのではない。** 記事系の対談は編集部の
  // ままなので、両方を残してある。良さそうなら残りのインタビューも寄せる
  "ま": {
    name: "ゆめ",
    role: "ゆめさく広報",
    kind: "host",
    // 差し替えたら名前も変える。同じ名前のまま中身だけ替えると、
    // 配信側に前の絵が残って、直したのに変わらないように見える
    avatarUrl: "/lab/interviewer-maho-tanaka-2.webp",
  },
  // 答える側。片方だけ話者ブロックにすると、質問と答えで行の形が変わって
  // 記事が半分だけ直しかけに見える
  // 顔写真を出す人だけをここに書く。**答える側は登録しなくても出る。**
  // 【生田講師】【関さん】のように敬称や肩書きで終わる名前は、表に無くても
  // 話者として扱う。インタビューは回ごとに相手が変わるので、1人増えるたびに
  // 表を触る作りにすると、本文を直したのに名前が出ない食い違いが必ず起きる
  // 敬称でも肩書きでも終わらないので、自動では話者にならない。バナーの表記が
  // これなので、ここに合わせる。別の名前を当てると同じ人が2つの名前でサイトに出る
  "主婦・フリーランスの方": { name: "主婦・フリーランスの方", kind: "guest" },
  // 名前を出さない受講生の声。記事では「話してくれた方がいます」のように
  // 匿名で引いているので、名前を当てずにこの表記で吹き出しにする
  "受講生": { name: "受講生", kind: "guest" },
  "生田講師": {
    name: "生田講師",
    kind: "guest",
    avatarUrl: "/lab/interviewee-rikuto-ikuta.webp",
  },
  "岡田講師": {
    name: "岡田講師",
    kind: "guest",
    avatarUrl: "/lab/interviewee-okada.webp",
  },
  // 受講生は顔を出さない。インタビュー動画の枠と同じく、ぼかした写真にする
  "もうさん": {
    name: "もうさん",
    kind: "guest",
    avatarUrl: "/lab/interviewee-mou-blur.webp",
  },
  // 代表記事で答える側。**自動判定には乗らない。** DIALOGUE_NAME は
  // さん / 講師 / メンター / 先生 / くん / ちゃん で終わる名前しか拾わないので、
  // 肩書きだけのこの表記は必ずここに要る。
  //
  // 顔写真ではなく線画を当てている。公開面では代表の氏名も顔も出していないので、
  // 写真を出すと記事の側の決めを UI が追い越す。絵なら、対話の形を保ったまま、
  // 出していないものを出さずに済む
  "代表": {
    name: "代表",
    kind: "author",
    avatarUrl: "/lab/speaker-daihyo-man02.webp",
  },
  /*
    代表記事で代表を止める側。中の人は「ま」と同じゆめだが、**記号を
    分けてある。** 講師インタビューの「ま」は聞き手なので右に置く。同じ
    記号にすると、どちらかの記事で左右が逆になる
  */
  "広報": {
    name: "ゆめ",
    role: "ゆめさく広報",
    kind: "challenger",
    avatarUrl: "/lab/interviewer-maho-tanaka-2.webp",
  },
};

const DIALOGUE_PATTERN = /^【([^】]{1,12})】\s*/;

/**
 * 表に無い名前を話者として扱ってよいか。
 *
 * 敬称か肩書きで終わるものだけを人として読む。これが無いと
 * 【プロフィール】や【Aさんプロフィール】のような見出しまで話者になり、
 * 既存の記事の本文が名乗りに化ける。
 */
const DIALOGUE_NAME = /(さん|講師|メンター|先生|くん|ちゃん)$/;

function resolveDialogueSpeaker(key: string) {
  const known = DIALOGUE_SPEAKERS[key];
  if (known) return known;
  if (DIALOGUE_NAME.test(key)) {
    return { name: key, kind: "guest" as const };
  }
  return undefined;
}

/**
 * 26px だと顔が潰れて誰か分からなかった。名前行は往復ごとに1回しか
 * 出ないので、少し大きくしても行頭がうるさくならない。
 */
/**
 * 段落が「内部リンク 1 本だけ」でできているかを見る。
 *
 * 記事の中で次の回へ送るとき `→ [題名](/lab/founder/xxx)` と書いている。
 * これを素のリンクのまま出すと、下線の引かれた 1 行が本文に埋もれて、
 * 押せることにも次の回があることにも気づかれない。
 *
 * **文中のリンクはカードにしない。** 段落が矢印とリンクだけでできている
 * ときに限る。`詳しくは[ここ](/x)を見てください` まで拾うと、読んでいる
 * 途中で本文が切れてカードが挟まる。
 *
 * 矢印は `→` `->` と、その後ろの空白だけを許す。他の字が付いていたら
 * それは文なので対象外にする。
 */
function readInternalLinkOnlyParagraph(
  node: any,
): { href: string; label: string } | null {
  /*
    **react-markdown が渡してくるのは hast。** リンクは `type: "link"` と
    `.url` ではなく、`type: "element"` / `tagName: "a"` / `properties.href`
    で来る。mdast の名前で書いていた間はここが一度も当たらず、カードの道に
    入らないまま素のリンクが出ていた。両方受けて、版が変わっても落ちない形にする
  */
  const isLink = (c: any) =>
    c?.type === "link" || (c?.type === "element" && c?.tagName === "a");
  const hrefOf = (c: any): string =>
    c?.url ?? c?.properties?.href ?? "";

  const kids = (node?.children ?? []).filter(
    (c: any) => !(c.type === "text" && /^\s*$/.test(c.value ?? "")),
  );
  let link: any = null;
  if (kids.length === 1 && isLink(kids[0])) {
    link = kids[0];
  } else if (
    kids.length === 2 &&
    kids[0].type === "text" &&
    /^\s*(→|->)\s*$/.test(kids[0].value ?? "") &&
    isLink(kids[1])
  ) {
    link = kids[1];
  }
  if (!link) return null;
  const href: string = hrefOf(link);
  // 内部リンクだけ。外は既存の「URL だけの段落」の道が見ている
  if (!href.startsWith("/")) return null;
  // 文字、インラインコード、画像の alt を hast と mdast のどちらからも読む
  const textOf = (c: any): string =>
    typeof c?.value === "string"
      ? c.value
      : (c?.alt ?? c?.properties?.alt ?? (c?.children ?? []).map(textOf).join(""));
  return { href, label: textOf(link).trim() };
}

function DialogueAvatar({ avatarUrl, name }: { avatarUrl?: string; name: string }) {
  if (!avatarUrl) return null;
  return (
    <img
      src={avatarUrl}
      alt={name}
      width={34}
      height={34}
      loading="lazy"
      className="h-[34px] w-[34px] shrink-0 rounded-full object-cover"
    />
  );
}

function DialogueLine({
  speakerKey,
  children,
}: {
  speakerKey: string;
  children: React.ReactNode;
}) {
  const speaker = resolveDialogueSpeaker(speakerKey)!;
  const kind = DIALOGUE_KINDS[speaker.kind];

  // 枠線・地色・アイコン・名前を全部足すと、同じ役目のものが4つ重なって飾りすぎになる。
  // 話者を示すのは名前ひとつで足りる。行頭は揃えたまま、濃さと大きさで分ける。
  //
  // data-dialogue は飾りではない。隣り合う同じ話者の名前行を消す手がかり
  return (
    <div data-dialogue={speaker.kind} className={kind.row}>
      {/* 顔は吹き出しの外に置く。中に入れると1行目だけ字が回り込んで、
          2行目から行頭がずれる */}
      <div className="mt-6 shrink-0 [[data-dialogue=host]+[data-dialogue=host]_&]:invisible [[data-dialogue=guest]+[data-dialogue=guest]_&]:invisible [[data-dialogue=challenger]+[data-dialogue=challenger]_&]:invisible [[data-dialogue=author]+[data-dialogue=author]_&]:invisible">
        <DialogueAvatar avatarUrl={speaker.avatarUrl} name={speaker.name} />
      </div>
      <div className={kind.content}>
        <div className={kind.head}>
          <span className={kind.name}>{speaker.name}</span>
          {/* 肩書きは名前と同じ濃さで出さない。81 往復のインタビューで
              2 つ並べて強く出すと、行頭が毎回うるさくなる */}
          {speaker.role ? (
            <span className="text-[15px] font-normal text-sub">
              {speaker.role}
            </span>
          ) : null}
        </div>
        <div className={`inline-block text-left ${kind.bubble}`}>{children}</div>
      </div>
    </div>
  );
}

/**
 * 段落の先頭が話者の記号かを見る。children の先頭が文字列でないとき
 * （強調やリンクで始まる行）は対象外にする。記号は必ず素の文字で書く。
 */
/** 吹き出しの頭に置かれた絵文字 (ロボットなど) と、続く空白 */
const DIALOGUE_LEADING_EMOJI = /^\s*(?:\p{Extended_Pictographic}\uFE0F?\u200D?)+\s*/u;

function readDialogueSpeaker(
  children: React.ReactNode,
): { speakerKey: string; rest: React.ReactNode } | null {
  const list = Children.toArray(children);
  const first = list[0];
  if (typeof first !== "string") return null;

  const matched = first.match(DIALOGUE_PATTERN);
  if (!matched) return null;

  const speakerKey = matched[1];
  if (!resolveDialogueSpeaker(speakerKey)) return null;

  const trimmed = first
    .slice(matched[0].length)
    .replace(DIALOGUE_LEADING_EMOJI, "");
  const rest = trimmed ? [trimmed, ...list.slice(1)] : list.slice(1);
  return { speakerKey, rest };
}

// 段落がURLのみかどうかをチェック
function isUrlOnlyParagraph(children: React.ReactNode): string | null {
  if (!children) return null;

  // childrenが配列の場合
  if (Array.isArray(children)) {
    // 1つの要素のみの場合
    if (children.length === 1) {
      const child = children[0];

      // React要素（aタグ）の場合
      if (child && typeof child === "object" && "props" in child) {
        const props = (child as any).props;
        if (props?.href) {
          const href = props.href;
          const text = props.children;
          // リンクテキストがURLと同じ、またはURLで始まる場合
          if (
            typeof text === "string" &&
            (text === href || text.startsWith("http"))
          ) {
            return href;
          }
        }
      }
    }
    return null;
  }

  // 単一の要素の場合
  if (children && typeof children === "object" && "props" in children) {
    const props = (children as any).props;
    if (props?.href) {
      const href = props.href;
      const text = props.children;
      if (
        typeof text === "string" &&
        (text === href || text.startsWith("http"))
      ) {
        return href;
      }
    }
  }

  return null;
}

// Contributor の型定義
export interface ContributorData {
  role: string;
  creator: {
    name: string;
    slug?: string | null;
    title?: string | null;
    avatarUrl?: string | null;
  };
}

// デフォルト引数で `[]` を生成すると、親の再描画ごとに contributorMap と
// markdownComponents が作り直され、react-markdown 配下の Custom Element まで
// 再マウントされる。図解の操作状態を保つため、空配列の identity を固定する。
const EMPTY_CONTRIBUTORS: ContributorData[] = [];

export interface CodeExecutionResult {
  output: string;
  error: string | null;
  image?: string | null;
}

export interface MarkdownContentProps {
  content: string;
  className?: string;
  /**
   * 本文中の raw HTML を要素として描画するか。
   *
   * 既存の記事本文との互換性のためデフォルトは true。クイズのように JSX / TypeScript
   * の `<...>` を説明文として受け取る境界では false にして、文字どおり表示する。
   *
   * "svg-figures" は、本文に直に書いた自己完結の SVG の図 (`<div data-lesson-diagram>` と
   * `<div data-zukai-figure>` の塊) だけを、許した要素と属性に絞って描く (rehypeSvgFigures)。
   * ほかの生の HTML は false と同じく文字のまま出す。クラウドエディターの板で使う。
   */
  allowRawHtml?: boolean | "svg-figures";
  /** contributor 情報（:::quote[slug] で動的表示に使用） */
  contributors?: ContributorData[];
  /** コード実行ハンドラー（:::runnable で使用） */
  onCodeExecute?: (code: string, language: string) => Promise<CodeExecutionResult>;
  /**
   * :::runnable のMonacoを初期mountせず、コード本文を含む軽量previewにする。
   * 多数の実行blockを持つ長文向け。各blockの編集操作で個別にmountする。
   */
  deferRunnableEditors?: boolean;
  /**
   * :::runnable を実行できない素のコードの囲みとして描く (実行のボタンも編集の窓も出さない)。
   * 本文を描くのと同じ remark の directive の解釈で拾うので、属性つき、字下げ、:: の書き方も漏れない。
   * 体験の人など「見る」だけの人に使う
   */
  lockRunnable?: boolean;
  /**
   * :::faqitem の質問の題の描き方。渡さなければ字のまま。
   * スマホの狭い枠で「るのか」「か」だけが次の行に落ちないよう、使う側の文節で折る部品 (LgPhrase など) を渡す
   */
  renderFaqQuestion?: (q: string) => React.ReactNode;
  /**
   * Defer Prism tokenization for offscreen static code fences and let the
   * browser skip style/layout/paint inside offscreen code widgets. The complete
   * article and plain code remain in SSR output.
   */
  deferOffscreenCodeRendering?: boolean;
  /**
   * 用語ハイライト辞書（モバイル軽量化のためデフォルトは undefined = OFF）。
   * 渡すとレンダリング時に AST レベルで用語を検出してホバー説明を付与する。
   */
  glossary?: GlossaryEntry[];
  /**
   * 用語に印を付ける範囲。"page" (既定) は 1 用語を頁で 1 回だけ、"section" は h2 の節ごとに最初の 1 回。
   * 長い教材 (学習ポータルのテキスト) は、節を飛ばして読む人が節の中で語を引けるよう "section" にする
   */
  glossaryScope?: "page" | "section";
  /**
   * 1 つの段落 (p、li、dd、td など) に付ける印の上限。用語の点線と語の前の印を合わせて数え、同じ語に付いた点線と印は 1 つと数える。
   * 用語の点線を先に残し、残りの枠に語の前の印を前から入れる。枠から外れた用語は、同じ節の次の段落で付く。
   * 無ければ上限なし (既定。Git 辞典やレッスンは今までどおり)
   */
  marksPerBlock?: number;
  /**
   * 前の描き手 (同じ頁のゴールの行など) で既に付いた印。用語の点線は語 (GlossaryEntry.term) で、語の前の印は inlineMarksOfLine が返す鍵で渡す。
   * ここに入った語には、この描き手では印を付けない (頁で最初の 1 回だけにする)
   */
  marksSeen?: InlineMarksSeen;
  /** この字 (教材の題など) に出ている語には、語の前の印を付けない。用語の点線は glossary から外して渡す */
  markFreeTitle?: string;
  /**
   * インライン編集用に、各 top-level ブロックに `data-md-block-index` を付与し、
   * 各ブロックの Markdown ソース範囲を `onSourceMapBuilt` で通知する。
   *
   * 用途: ダブルクリックで段落単位の Markdown 編集を実装するときに使う。
   * 編集側がブロック単位の source 範囲を知っていれば、全文から該当範囲を
   * 切り出し → 編集 → splice で戻す、が安全に成立する。
   */
  enableSourceMap?: boolean;
  /** enableSourceMap が true のとき、レンダリング時に呼ばれる */
  onSourceMapBuilt?: (blocks: MarkdownBlockSource[]) => void;
  /**
   * `::tweet[URL]` に出す X の投稿。サーバーで取ってきたものを ID ごとに渡す。
   * 渡さない画面では、カードの代わりに元の投稿へのリンクを出す
   */
  tweets?: TweetEmbedMap;
  /**
   * 「## 読み方のコツ」の節を 1 つの囲みに組み直す (学習パスの本文だけが付ける)。無ければ今のまま。
   * 組み直した節は directive-tips と directive-tips-(lead|compare|good|bad|faq|qa) の要素になるので、描き方は components で渡す
   */
  readingTips?: boolean;
  /**
   * 本文の字を文節に切る関数。渡すと、切れ目に <wbr> を入れる (コードと数と単位の塊の中は割らない)。
   * 置く側は word-break: keep-all を当てる。渡す関数は呼び出しをまたいで同じもの (モジュールの関数) にする
   */
  phraseSegment?: (text: string) => string[];
  /**
   * クイズの答えの段落 (選択肢 A から D の箇条書きの直後の「答えは…」) を directive-quizanswer の要素にする。無ければ今のまま。
   * 描き方は components の "directive-quizanswer" で渡す (渡さなければ素の段落のまま出る)
   */
  foldQuizAnswers?: boolean;
  /** 本文の要素の描き方を足す・差し替える (今は読み方のコツの部品だけが使う) */
  components?: Record<string, (props: any) => React.ReactNode>;
  /** `:::video` の再生ボタンが押されたとき。src は本文に書いた URL のまま */
  onVideoPlay?: (src: string) => void;
}

/** 子要素 (強調やリンク) の中の字まで連結して取り出す */
function nodeText(node: React.ReactNode): string {
  return Children.toArray(node)
    .map((part) => {
      if (typeof part === "string" || typeof part === "number") return String(part);
      if (isValidElement(part)) return nodeText((part.props as { children?: React.ReactNode }).children);
      return "";
    })
    .join("");
}

/**
 * 数と記号と単位 1 語だけのセル (1800、$0.15、(1,200)、12.5%、2026-10-15、1,200 円 など) か。
 * 狭い欄で「1800」が「180」と「0」に割れないよう、このセルだけ折らない (表は横に送れる入れ物に入っている)。
 * 全体が 16 字以内で空白は 1 つまで。長い説明文のセルは対象にしない
 */
function isNumericCell(children: React.ReactNode): boolean {
  return isNumericText(nodeText(children).trim());
}
function isNumericText(text: string): boolean {
  if (text.length === 0 || text.length > 16) return false;
  return /^[¥$€£+\-~(]*[0-9][0-9.,:/%+\-~()]*(\s?[^\s0-9]{1,3})?$/.test(text);
}

type HastLike = { tagName?: string; value?: string; children?: HastLike[] };
function hastText(node: HastLike | undefined): string {
  if (!node) return "";
  if (typeof node.value === "string") return node.value;
  return (node.children ?? []).map(hastText).join("");
}
function hastRows(node: HastLike | undefined, section: "thead" | "tbody"): HastLike[][] {
  const part = (node?.children ?? []).find((c) => c.tagName === section);
  return (part?.children ?? [])
    .filter((c) => c.tagName === "tr")
    .map((tr) => (tr.children ?? []).filter((c) => c.tagName === "td" || c.tagName === "th"));
}

/**
 * 表の記入欄。本文に「右の2列を自分で埋めてください」と書いて空欄の列を置く記事がある
 * (24 時間表、1 週間表)。読むだけの面では埋められないので、本文の行が全部空の列は入力欄にする。
 * 一部の行だけ空の表は、書き手がわざと空けたものなので触らない。保存はせず、その場で書けるだけ
 */
type TableFill = { headers: string[]; columns: Set<number> };

/**
 * 狭い画面での表の組み方。決めるのは表の形 (列の数、見出しの有無、セルの字の長さ) だけで、冊ごとには決めない。
 * 見せ方は読む面の CSS が持つ (テキストの本文は textbook-reading.css。ほかの面は印を読まないので今までの表のまま)。
 * - cards 1 行を 1 枚の札にし、1 列目を札の題、残りを「列の見出し」と値の組で縦に並べる。3 列以上の文の表と、1 列目に長い語のある 2 列の文の表
 * - scroll 横に送る表のまま。見出しの無い列がある表と、数やコードが半分以上で文のセルの無い表 (数を列で見比べる表)
 * - grid 今までの表のまま。1 列目の短い 2 列の表と、1 列目のほかが短いセル (10 字まで) だけで狭い画面にも収まる表
 */
export type TableLayout = "cards" | "scroll" | "grid";
/** 字の長いセルの列が、狭い画面で読める細さの下限 (和文の字数)。これより細いと 1 行が 2〜3 字になる */
const LONG_COLUMN_MIN = 7;
/** スマホ幅 (390) の本文に表が収まる幅 (和文の字数)。セルの左右の空き (1 列に約 0.8 字) を含めて数える */
const NARROW_TABLE_MAX = 20;
/** 2 列の表の 1 列目が、説明の列を細くせずに並べられる長さ (和文の字数)。8 字 (「受け取りを伝える」) だと、15px の字で説明の列の長い句がはみ出た */
const TERM_COLUMN_MAX = 7;
/** 数の表の中で、文として読ませるセルの長さの下限 (和文の字数)。「完了」「済み」のような印の語は数えない */
const TEXT_CELL_MIN = 6;
/** 札の中で、見出しと値の組を横に並べる値の長さ (和文の字数)。3 列の格子の 1 枠 (スマホで約 6 字) に収まる長さ */
const CARD_SHORT_MAX = 4;

function isCodeOnlyCell(cell: HastLike): boolean {
  const kids = (cell.children ?? []).filter((c) => c.tagName || (c.value ?? "").trim());
  return kids.length > 0 && kids.every((c) => c.tagName === "code");
}

export function tableLayout(node: HastLike | undefined): TableLayout {
  const head = hastRows(node, "thead")[0] ?? [];
  const body = hastRows(node, "tbody");
  const cols = Math.max(head.length, ...body.map((r) => r.length), 0);
  if (cols < 2 || body.length === 0) return "grid";
  const headers = head.map((c) => hastText(c).trim());
  // 2 列目から先の見出しが空か数 (2020 | 2021 | 2022) なら、1 行目は見出しではなく中身。札の名前にできないので送る
  const named = headers.slice(1).filter((h) => h && !isNumericText(h));
  if (named.length < cols - 1) return "scroll";
  const values = body.flatMap((r) => r.slice(1));
  const isData = (c: HastLike) => isNumericText(hastText(c).trim()) || isCodeOnlyCell(c);
  const dataLike = values.filter(isData).length;
  const hasLong = values.some((c) => cellWidth(hastText(c).trim()) > SHORT_CELL_MAX);
  // 数を列で見比べる表 (線表、月ごとの数) は送る。ただし数のほかに文のセル (7 字以上。リスクの表の「対応」) があると、
  // その列が右に切れて読めないので、下の幅の見積もりに回す
  const hasText = values.some((c) => !isData(c) && cellWidth(hastText(c).trim()) > TEXT_CELL_MIN);
  if (dataLike * 2 >= values.length && !hasText) return "scroll";
  // 2 列の表は、1 列目が短い語 (7 字まで) なら用語と説明の表として読める。1 列目に長い語 (「ソーシャルエンジニアリング」) があると
  // その語が折れずに幅を取り、説明の列が細く切れて横に送る形になったので札にする
  if (cols === 2) {
    const firstWidest = Math.max(0, ...body.map((r) => cellWidth(hastText(r[0]).trim())));
    const longValue = body.some((r) => r[1] && cellWidth(hastText(r[1]).trim()) > SHORT_CELL_MAX);
    return firstWidest > TERM_COLUMN_MAX && longValue ? "cards" : "grid";
  }
  // 3 列以上で長いセル (11 字以上) が 1 つでもあれば札にする。字数の見積もりでは収まるはずの表 (QCD の表) も、
  // 実際は長いセルの列が 7 字ほどに細り「質を」のような 2 字の行が出た
  if (hasLong) return "cards";
  // 短いセルだけの表の、狭い画面での幅の見積もり。短いセルの列は折らないのでその字数、長いセルの列は読める細さの下限
  let width = 0;
  for (let i = 0; i < cols; i++) {
    const cells = [head[i], ...body.map((r) => r[i])].filter((c): c is HastLike => !!c);
    const widest = Math.max(0, ...cells.map((c) => cellWidth(hastText(c).trim())));
    width += Math.min(widest, widest > SHORT_CELL_MAX ? LONG_COLUMN_MIN : SHORT_CELL_MAX) + 0.8;
  }
  return width > NARROW_TABLE_MAX ? "cards" : "grid";
}

type TableMeta = { headers: string[]; layout: TableLayout; fill: TableFill | null };
const TableFillContext = createContext<TableMeta | null>(null);

function tableMeta(node: HastLike | undefined): TableMeta {
  const fill = tableFill(node);
  return {
    headers: (hastRows(node, "thead")[0] ?? []).map((c) => hastText(c).trim()),
    // 記入欄の表は入力欄を列にそろえて書くので、札にしない
    layout: fill ? "grid" : tableLayout(node),
    fill,
  };
}

function tableFill(node: HastLike | undefined): TableFill | null {
  const body = hastRows(node, "tbody");
  if (body.length === 0) return null;
  const headers = (hastRows(node, "thead")[0] ?? []).map((c) => hastText(c).trim());
  const columns = new Set<number>();
  const width = Math.max(...body.map((r) => r.length));
  for (let i = 0; i < width; i++) {
    if (body.every((r) => r[i] && hastText(r[i]).trim() === "" && !(r[i].children ?? []).some((c) => c.tagName))) columns.add(i);
  }
  // 全部の列が空なら何の表か分からないので、入力欄にしない
  if (columns.size === 0 || columns.size === width) return null;
  return { headers, columns };
}

function TableRow({ children, ...props }: React.ComponentProps<"tr">) {
  const meta = useContext(TableFillContext);
  const fill = meta?.fill;
  let out = children;
  if (fill) {
    const cells = Children.toArray(children).filter(isValidElement);
    const first = nodeText((cells[0]?.props as { children?: React.ReactNode } | undefined)?.children).trim();
    out = cells.map((cell, i) =>
      cell.type === "th" || !fill.columns.has(i)
        ? cell
        : cloneElement(cell as React.ReactElement<Record<string, unknown>>, {
            "data-fill-label": [first, fill.headers[i]].filter(Boolean).join(" ") || `${i + 1}列目`,
          }),
    );
  } else if (meta?.layout === "cards") {
    // 札の 2 列目から先は、値の上に列の見出しを添える (CSS が ::before で attr(data-label) を出す)。
    // 4 字までの値には data-card-short を付け、CSS が見出しと値の組を横に並べる
    out = Children.toArray(children)
      .filter(isValidElement)
      .map((cell, i) => {
        if (i === 0 || !meta.headers[i]) return cell;
        const text = nodeText((cell.props as { children?: React.ReactNode }).children).trim();
        const short = text.length > 0 && cellWidth(text) <= CARD_SHORT_MAX;
        return cloneElement(cell as React.ReactElement<Record<string, unknown>>, {
          "data-label": meta.headers[i],
          ...(short ? { "data-card-short": "" } : {}),
        });
      });
  }
  return (
    <tr className="border-y border-main" {...props}>
      {out}
    </tr>
  );
}

function TableProvider({ node, children, ...props }: React.ComponentProps<"table"> & { node?: HastLike }) {
  const meta = tableMeta(node);
  return (
    <TableFillContext.Provider value={meta}>
      <ScrollableTable layout={meta.layout} {...props}>
        {children}
      </ScrollableTable>
    </TableFillContext.Provider>
  );
}

export function MarkdownContent({
  content,
  className = "",
  allowRawHtml = true,
  contributors = EMPTY_CONTRIBUTORS,
  onCodeExecute,
  deferRunnableEditors = false,
  lockRunnable = false,
  renderFaqQuestion,
  deferOffscreenCodeRendering = false,
  glossary,
  glossaryScope = "page",
  marksPerBlock,
  marksSeen,
  markFreeTitle,
  enableSourceMap,
  onSourceMapBuilt,
  tweets,
  onVideoPlay,
  readingTips,
  phraseSegment,
  foldQuizAnswers,
  components: extraComponents,
}: MarkdownContentProps) {
  /* 部品の組み立て直しを呼ばないよう、最新の関数は ref から読む。
     書き換えは確定した描画のあとだけ。描画中に書くと、中断された描画の
     関数を前の画面のクリックが読む */
  const onVideoPlayRef = useRef(onVideoPlay);
  useEffect(() => {
    onVideoPlayRef.current = onVideoPlay;
  }, [onVideoPlay]);
  // SSR では辞書ハイライトを無効化する (hydration mismatch 回避)
  // クライアント側でマウント後に有効化する。
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  // 辞書 matcher は glossary が渡されたときだけ構築 (毎レンダー再計算しないよう memo)
  // hydrated === false の間も木の上では通す。下線は rehypeUnwrapGlossary が字に戻すので
  // SSR の HTML は変わらず、考え方の印の位置だけがマウント後と同じになる
  const shownGlossary = useMemo(() => withoutSeenTerms(glossary, marksSeen?.terms), [glossary, marksSeen?.terms]);
  const glossaryMatcher = useMemo(() => {
    try {
      const m = buildGlossaryMatcher(shownGlossary);
      if (hydrated && typeof window !== "undefined" && glossary && glossary.length > 0) {
        // 用語数を console.debug で出しておく (パフォーマンス計測の手がかり)
        // eslint-disable-next-line no-console
        console.debug(
          `[MarkdownContent] glossary matcher built: ${glossary.length} entries, ${m?.sortedKeys.length ?? 0} keys`,
        );
      }
      return m;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[MarkdownContent] failed to build glossary matcher", err);
      return null;
    }
  }, [shownGlossary, hydrated]);
  // onCodeExecute を ref で保持して安定した参照を維持
  const onCodeExecuteRef = useRef(onCodeExecute);
  onCodeExecuteRef.current = onCodeExecute;

  /*
   * directive-runnable (RunnableCodeBlock) の instanceIndex 用カウンタ。
   * 内容が完全に同じコードのブロックが 1 頁に複数あると、内容だけでは
   * state の見分けが付かないので、頁の中で何番目に出てきたかを数えて渡す。
   * このレンダーの間だけ有効なので、毎レンダーの頭でいったん空にする
   * (レンダーは document.content の並び順どおりに directive を呼ぶので、
   * ここで数える順番は毎回同じになる)。
   */
  const runnableOccurrenceRef = useRef<Map<string, number>>(new Map());
  runnableOccurrenceRef.current.clear();

  // 画像/Mermaid のライトボックス
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxSrc(null);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxSrc]);

  // contributors を slug でひける Map に正規化 (毎レンダーの Array.find を回避)。
  // 省略時は EMPTY_CONTRIBUTORS により identity が安定する。
  const contributorMap = useMemo(() => {
    const m = new Map<string, ContributorData>();
    for (const c of contributors) {
      if (c?.creator?.slug) m.set(c.creator.slug, c);
    }
    return m;
  }, [contributors]);

  // 本文の先頭に置かれた図解は、そのページの LCP 候補になる。該当する
  // 1枚だけを high priority にし、本文中の全図解を先読みして回線を奪わない。
  const leadingFigureId = useMemo(() => {
    const firstLine = content.trimStart().split(/\r?\n/, 1)[0] ?? "";
    const label = firstLine.match(/^:{2,3}figure\[([a-z0-9-]{1,64})\]/);
    if (label) return label[1];
    return (
      firstLine.match(
        /^:{2,3}figure\{[^}]*\bsrc=["']([a-z0-9-]{1,64})["'][^}]*\}/,
      )?.[1] ?? null
    );
  }, [content]);

  // markdownComponents / renderInlineCode / renderCodeBlock は親の state 更新
  // (例: lightboxSrc トグル) のたびに再生成すると react-markdown が全ディレクティブの
  // サブツリーを再 reconcile してしまうため useMemo で固定する。
  // - setLightboxSrc / onCodeExecuteRef は安定参照なので deps 不要
  // - lightboxSrc 自体は本オブジェクト内で読まれていない (ライトボックス JSX は return 側)
  // - glossary は rehypeGlossary プラグイン側で消費しているため deps 不要
  // 依存は contributorMap のみ。
  const markdownComponents = useMemo<Components & Record<string, any>>(() => {
    // slug から contributor を検索
    const findContributorBySlug = (slug: string): ContributorData | undefined => {
      return contributorMap.get(slug);
    };
    // インラインコード用（ブロックは pre 側で処理されるのでここでは触らない）
    const renderInlineCode = ({ children, node: _node, ...props }: any) => {
      // Figma: node 15:4713 — Highlight
      // 背景は緑10% / 文字は text-primary / 14px / tracking 1.4px。
      // **直書きにしない。** #037F40 を直に書いていたとき、ダークでは
      // 地との差が 1.81 まで落ちて読めなくなっていた
      // 行の高さは styles/index.css の :not(pre) > code.font-mono で 1.4 に固定している (class にすると記事の SSR が数 KB 増える)。
      // Python や CRUD のような短い識別子は語の途中で改行させない。
      // 画面幅を超える長い文字列のみ折り返す（break-words = overflow-wrap: break-word）
      const short = shortInlineCode(cellText(children) ?? "");
      return (
        <code
          className="px-1.5 py-1 rounded text-[14px] font-mono bg-primary/10 text-primary tracking-[0.1em] break-words"
          data-short-code={short}
          {...props}
        >
          {!short && typeof children === "string" ? withPathBreaks(children) : children}
        </code>
      );
    };

    // コードブロック用（pre > code構造）
    const renderCodeBlock = ({ children, ...props }: any) => {
      // children から code 要素を取得
      const codeElement = children?.props;
      if (!codeElement) {
        return <pre {...props}>{children}</pre>;
      }

      const className = codeElement.className || "";
      const match = /language-(\w+)/.exec(className);
      const language = match ? match[1] : "text";
      // codeElement.children が React ノードの配列の場合があるので安全に文字列化
      // 子に React 要素が混じっていても、その中のテキストを再帰的に拾って結合する
      const rawChildren = codeElement.children;
      const stringifyChildren = (node: any): string => {
        if (node == null || typeof node === "boolean") return "";
        if (typeof node === "string" || typeof node === "number") return String(node);
        if (Array.isArray(node)) return node.map(stringifyChildren).join("");
        if (node.props?.children) return stringifyChildren(node.props.children);
        return "";
      };
      const codeString = stringifyChildren(rawChildren).replace(/\n$/, "");

      if (language === "mermaid") {
        return <MermaidBlock code={codeString} onZoom={setLightboxSrc} />;
      }

      // コードフェンスの meta から file/title 属性を抽出
      // 例: ```java file="src/main/Foo.java"  /  ```text title="luagate_pj_ec_site/"
      const meta: string =
        codeElement.node?.data?.meta ?? codeElement["data-meta"] ?? "";
      const fileMatch = /(?:file|filename|title)\s*=\s*["']([^"']+)["']/.exec(
        meta,
      );
      const headerLabel = fileMatch?.[1];
      // ```output は命令を打ったあとに出る結果。打つ物ではないので、「ターミナル」の帯も写しの釦も出さない
      const isOutput = language === "output";
      const isPlainLanguage = !match || language === "text" || language === "txt" || isOutput;
      // 言語の無い塊のうち、罫線や記号の図でない物 (対応表のような文) だけに印を付ける。
      // 印のある塊は画面の側で折り返してよい。罫線の図は折ると崩れるので印を付けず、横に送る
      const proseBlock = isPlainLanguage && !isOutput && !isTextArtBlock(codeString);

      const container = (
        <CodeContainer
          filename={headerLabel ?? `example.${language}`}
          code={codeString}
          language={language}
          showAiButton={false}
          // 言語もファイル名も無い囲み (``` だけ、```text) は、見出しの帯に出す名前が「プレーンテキスト」しか無い。帯ごと出さない
          showHeader={!isOutput && (Boolean(headerLabel) || !isPlainLanguage)}
          deferSyntaxHighlighting={deferOffscreenCodeRendering}
          // 白地にファイル名の帯、構文は syntaxColors.ts の8色。
          // ベージュの窓 (variant="figure") は図解の中のコードと同じ橙1色で、
          // 図解との地続き感は出るが、プロパティも値も記号も同じ色に落ちて
          // 行の構造が読めない。本文のコードは眺めるものではなく読んで写す
          // ものなので、読みやすさを取る。図解は図解で橙のままでよく、
          // むしろ「図の中の説明」と「実際に書くコード」の区別がつく
          variant="editor"
        />
      );
      if (isOutput) return <div data-code-output="">{container}</div>;
      return proseBlock ? <div data-code-prose="">{container}</div> : container;
    };

    return {
      code: renderInlineCode,
      pre: renderCodeBlock,
      span: ({ children, node: _node, ...props }: any) => {
        const iconName = (props as any)["data-icon"];
        if (iconName === "check") {
          return (
            <CheckCircle
              className="inline-block align-text-bottom text-primary"
              size={16}
              strokeWidth={2.5}
              aria-label="同梱済み"
            />
          );
        }
        if (iconName === "pencil") {
          return (
            <Pencil
              className="inline-block align-text-bottom text-accent-400"
              size={16}
              strokeWidth={2.5}
              aria-label="自分で書く"
            />
          );
        }
        return <span {...props}>{children}</span>;
      },
      h1: ({ children, node: _node, ...props }) => (
        <h1
          className="font-sans text-2xl md:text-[32px] font-bold leading-tight text-main mt-8 mb-6 first:mt-0"
          {...props}
        >
          {children}
        </h1>
      ),
      h2: ({ children, node: _node, ...props }) => (
        <MarkdownSectionHeading {...props}>{children}</MarkdownSectionHeading>
      ),
      h3: ({ children, node: _node, ...props }) => (
        <h3
          className="font-sans text-lg md:text-xl font-bold leading-tight text-main mt-12 mb-3 pb-2 border-b border-gray-200"
          {...props}
        >
          {children}
        </h3>
      ),
      p: ({ children, node, ...props }) => {
        // URLのみの段落はOGPLinkCard表示
        const urlOnly = isUrlOnlyParagraph(children);
        if (urlOnly) {
          return <OGPLinkCard url={urlOnly} phraseSegment={phraseSegment} />;
        }

        // 内部リンク 1 本だけの段落はカードにする。記事から記事へ送る
        // `→ [題名](/lab/...)` が本文に埋もれるのを止める
        const innerLink = readInternalLinkOnlyParagraph(node);
        if (innerLink) {
          return <OGPLinkCard url={innerLink.href} label={innerLink.label} phraseSegment={phraseSegment} />;
        }

        // nodeから直接URLを検出（remark-gfmによる自動リンク変換後）
        if (node?.children?.length === 1) {
          const child = node.children[0];
          if (child.type === "link" && child.children?.[0]?.type === "text") {
            const href = (child as any).url;
            const text = (child.children[0] as any).value;
            if (href && text && (text === href || text.startsWith("http"))) {
              return <OGPLinkCard url={href} phraseSegment={phraseSegment} />;
            }
          }
        }

        const dialogue = readDialogueSpeaker(children);
        if (dialogue) {
          return (
            <DialogueLine speakerKey={dialogue.speakerKey}>
              {dialogue.rest}
            </DialogueLine>
          );
        }

        return (
          <p
            className="relative top-px font-[Inter,'Noto_Sans_JP',sans-serif] text-base font-normal leading-[1.9] text-main my-6"
            {...props}
          >
            {children}
          </p>
        );
      },
      ul: ({ children, node: _node, ...props }) => (
        <ul
          className="list-disc list-outside pl-5 font-[Inter,'Noto_Sans_JP',sans-serif] text-[15px] font-normal leading-[1.9] text-main mb-4 space-y-2"
          {...props}
        >
          {children}
        </ul>
      ),
      ol: ({ children, node: _node, ...props }) => (
        <ol
          className="list-decimal list-outside pl-5 font-[Inter,'Noto_Sans_JP',sans-serif] text-[15px] font-normal leading-[1.9] text-main mb-4 space-y-2"
          {...props}
        >
          {children}
        </ol>
      ),
      li: ({ children, node: _node, ...props }) => (
        <li
          className="relative top-px font-[Inter,'Noto_Sans_JP',sans-serif] text-[15px] font-normal leading-[1.9] text-main marker:text-primary marker:text-lg [&_input[type='checkbox']]:w-5 [&_input[type='checkbox']]:h-5 [&_input[type='checkbox']]:mr-2 [&_input[type='checkbox']]:align-[-0.2em] [&:has(>input[type='checkbox'])]:list-none [&:has(>input[type='checkbox'])]:py-1.5 [&:has(>input[type='checkbox'])]:text-lg"
          {...props}
        >
          {children}
        </li>
      ),
      blockquote: ({ children, node: _node, ...props }) => (
        <blockquote
          className="relative my-8 px-6 py-5 rounded-2xl bg-primary/8 text-main not-italic"
          {...props}
        >
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-md bg-primary/15 text-primary text-sm font-bold font-noto mb-3"
            aria-hidden="true"
          >
            解説
          </span>
          <div className="space-y-3 text-base leading-[1.9] [&_p]:my-0">
            {children}
          </div>
        </blockquote>
      ),
      a: ({ children, node: _node, ...props }) => (
        <a
          className="text-primary hover:underline [overflow-wrap:anywhere]"
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children}
        </a>
      ),
      strong: ({ children, node: _node, ...props }) => (
        <strong className="font-bold text-main" {...props}>
          {children}
        </strong>
      ),
      em: ({ children, node: _node, ...props }) => (
        <em className="italic text-main" {...props}>
          {children}
        </em>
      ),
      // Figma 準拠 (node 2006:19229)
      // 色は Figma スペック値をハードコード（プロジェクトのトークンより Figma を優先）
      // - text: text-main (Figma Text/Main)
      // - border: border-main (Figma Border/Main)
      // - header bg: bg-second (Figma Surface/BackGround/Second)
      //
      // **ここも直書きにしない。** #363635 の文字はダークの地 #012e1a の
      // 上で差が 1.23 しかなく、表の中身が丸ごと読めなくなっていた
      // 折り返しは、語と数の途中では折らず、長い日本語は文節で折る (th と td だけ)。
      // 既定 (Safari と Firefox) は break-word。anywhere と違い列の最小幅を縮めないので、列が 1 字に潰れない。
      // auto-phrase が使える Chrome は、文節の切れ目で折り、カタカナ語と英単語も割らない。収まらない分は横に送る
      // - ヘッダ: px-6 py-3 / bold / leading 1.8
      // - セル: px-6 py-5 / regular / leading 1.8
      table: ({ children, node, ...props }) => (
        <TableProvider node={node as HastLike | undefined} {...props}>
          {children}
        </TableProvider>
      ),
      thead: ({ children, node: _node, ...props }) => (
        <thead {...props}>{children}</thead>
      ),
      tbody: ({ children, node: _node, ...props }) => (
        <tbody {...props}>{children}</tbody>
      ),
      tr: ({ children, node: _node, ...props }) => (
        <TableRow {...props}>{children}</TableRow>
      ),
      th: ({ children, node: _node, ...props }) => (
        <th
          data-short-cell={shortCellMark(children)}
          className="bg-second pl-1 pr-2 md:pr-4 py-3 text-left text-sm font-bold leading-[1.8] align-middle text-main tabular-nums [word-break:normal] [overflow-wrap:break-word] supports-[word-break:auto-phrase]:[word-break:auto-phrase] supports-[word-break:auto-phrase]:[overflow-wrap:normal]"
          {...props}
        >
          {children}
        </th>
      ),
      td: ({ children, node: _node, "data-fill-label": fillLabel, ...props }: React.ComponentProps<"td"> & { node?: unknown; "data-fill-label"?: string }) => (
        <td
          data-short-cell={shortCellMark(children)}
          className={`pl-1 pr-2 md:pr-4 ${fillLabel ? "py-2" : "py-5"} text-sm font-normal leading-[1.8] align-middle text-main tabular-nums [word-break:normal] [overflow-wrap:break-word] supports-[word-break:auto-phrase]:[word-break:auto-phrase] supports-[word-break:auto-phrase]:[overflow-wrap:normal]${isNumericCell(children) ? " whitespace-nowrap" : ""}`}
          {...props}
        >
          {fillLabel ? (
            <input
              type="text"
              aria-label={fillLabel}
              autoComplete="off"
              className="h-10 w-full min-w-[5.5rem] rounded-md border border-main bg-surface px-2 text-sm text-main outline-none transition-colors hover:border-[#05b45b] focus:border-[#05b45b] focus-visible:ring-2 focus-visible:ring-[#05b45b]/40"
            />
          ) : (
            children
          )}
        </td>
      ),
      // Markdown places images inside a paragraph. A block <div> here produced
      // invalid <p><div> markup that browsers reparsed before React hydrated.
      img: ({ src, alt, node: _node, ...props }) => (
        <img
          src={src}
          alt={alt || ""}
          className="block mx-auto my-6 max-w-[min(100%,28rem)] h-auto rounded-lg shadow-xs cursor-zoom-in transition-opacity hover:opacity-90"
          loading="lazy"
          onClick={() => {
            if (typeof src === "string") setLightboxSrc(src);
          }}
          {...props}
        />
      ),
      // 用語ハイライト (rehypeGlossary が <glossary-term> 要素に変換した部分)
      // relatedTerms は string のまま渡し、GlossaryTermInline 側で useMemo して
      // memo 化したコンポーネントの再描画を抑える。
      "glossary-term": ({
        children,
        term,
        matched,
        summary,
        related,
        detail,
        reading,
        category,
        difficulty,
        slug,
        aliases,
        "related-terms": relatedTerms,
      }: any) => (
        <GlossaryTermInline
          term={term}
          matched={matched}
          summary={summary}
          related={related}
          detail={detail}
          reading={reading}
          category={category}
          difficulty={difficulty ? Number(difficulty) : undefined}
          slug={typeof slug === "string" && slug.length > 0 ? slug : undefined}
          relatedTermsRaw={typeof relatedTerms === "string" ? relatedTerms : ""}
          aliasesRaw={typeof aliases === "string" ? aliases : ""}
        >
          {children}
        </GlossaryTermInline>
      ),
      // カスタムディレクティブ
      "directive-bad": ({ children, kind }: any) => (
        <BadExampleBlock kind={kind}>{children}</BadExampleBlock>
      ),
      "directive-good": ({ children, kind }: any) => (
        <GoodExampleBlock kind={kind}>{children}</GoodExampleBlock>
      ),
      "directive-faq": ({ children }: any) => <FaqBlock>{children}</FaqBlock>,
      "directive-faqitem": ({ children, q }: any) => (
        <FaqItem q={q} renderQuestion={renderFaqQuestion}>{children}</FaqItem>
      ),
      "directive-challenge": ({ children }: any) => (
        <ChallengeBlock>{children}</ChallengeBlock>
      ),
      "directive-point": ({ children, label }: any) => (
        <PointBlock label={label}>{children}</PointBlock>
      ),
      "directive-details": ({ children, label }: any) => (
        <DetailsBlock label={label}>{children}</DetailsBlock>
      ),
      "directive-sensei": ({ children }: any) => (
        <SenseiBlock>{children}</SenseiBlock>
      ),
      "directive-doubt": ({ children }: any) => (
        <DoubtBlock>{children}</DoubtBlock>
      ),
      "directive-answer": ({ children, ask, a, b }: any) => (
        <ReflectionCard ask={ask} a={a} b={b}>
          {children}
        </ReflectionCard>
      ),
      "directive-spirit": ({ children }: any) => (
        <SpiritBlock>{children}</SpiritBlock>
      ),
      "directive-hare": ({ children }: any) => (
        <HareBlock>{children}</HareBlock>
      ),
      "directive-tortoise": ({ children }: any) => (
        <TortoiseBlock>{children}</TortoiseBlock>
      ),
      "directive-error": ({ children }: any) => (
        <ErrorTipBlock>{children}</ErrorTipBlock>
      ),
      "directive-references": ({ children }: any) => (
        <ReferencesBlock>{children}</ReferencesBlock>
      ),
      "directive-quote": ({ children, label }: any) => {
        // label（slug）から contributor を動的に取得
        const contributor = label ? findContributorBySlug(label) : undefined;
        return (
          <QuoteBlock
            author={contributor?.creator.name}
            avatar={contributor?.creator.avatarUrl || undefined}
            title={contributor?.creator.title || undefined}
            label={label}
          >
            {children}
          </QuoteBlock>
        );
      },
      // 実行可能コードブロック
      "directive-runnable": ({ children, language, code: codeAttr }: any) => {
        // code 属性が渡されていればそれを優先（インデントが保持されている）
        let finalCode = typeof codeAttr === "string" ? codeAttr : "";
        let finalLanguage = language || "javascript";
        if (!finalCode) {
          const extracted = extractRunnableCode(children);
          if (!extracted) return null;
          finalCode = extracted.code;
          finalLanguage = language || extracted.language;
        }
        // 実行できない人には、ふつうのコードの囲みと同じ見た目で描く (実行の口を出さない)
        if (lockRunnable) {
          return (
            <CodeContainer
              filename={`example.${finalLanguage}`}
              code={finalCode}
              language={finalLanguage}
              showAiButton={false}
              showHeader
              deferSyntaxHighlighting={deferOffscreenCodeRendering}
              variant="editor"
            />
          );
        }
        // コードの内容をキーとして使用し、同じコードブロックを同じインスタンスとして維持。
        // 内容が完全に同じブロックが頁に複数あっても React の key が衝突しないよう、
        // 頁の中で何番目かを付ける (RunnableCodeBlock 側の instanceIndex と同じ数え方)。
        // RunnableCodeBlock 内部は language ではなく detectLanguage で正規化した言語で
        // state key を作るので、occurrence の数え方もそれに合わせる (そうしないと、
        // 宣言した language は違うが detectLanguage が同じ言語と見なす 2 本が
        // 両方 occurrenceIndex 0 になり、state を共有してしまう)
        const occurrenceLanguage = detectLanguage(finalCode, finalLanguage);
        const occurrenceKey = `${occurrenceLanguage}:${finalCode}`;
        const occurrenceIndex = runnableOccurrenceRef.current.get(occurrenceKey) ?? 0;
        runnableOccurrenceRef.current.set(occurrenceKey, occurrenceIndex + 1);
        // React の key は内容全体 + occurrenceIndex で衝突なく一意にする。
        // 頭 50 字だけだと、頭が同じで後ろが違う 2 本がどちらも occurrenceIndex 0 になり
        // key が衝突する (内容が違うので occurrenceKey の Map では別物として数えるため)
        const key = `runnable-${occurrenceKey}-${occurrenceIndex}`;
        return (
          <RunnableCodeBlock
            key={key}
            initialCode={finalCode}
            language={finalLanguage}
            onExecute={onCodeExecuteRef.current}
            deferEditorMount={deferRunnableEditors}
            instanceIndex={occurrenceIndex}
          />
        );
      },
      // インタラクティブチャート
      "directive-chart": ({ type, title }: any) => (
        <InteractiveChart type={type || "roadmap"} title={title} />
      ),
      "directive-flowchart": ({ title }: any) => (
        <InteractiveChart type="flowchart" title={title} />
      ),
      "directive-roadmap": ({ title }: any) => (
        <InteractiveChart type="roadmap" title={title} />
      ),
      // 触れる図解 (`::figure[array-index]` / `::figure{src="array-index"}`)
      // `:::video{src="..." title="..."}` で解説動画を貼る。
      // YouTube と Cloudflare Stream のどちらの URL でも受ける
      "directive-video": ({ src, title, label, poster }: any) => {
        // `:::video{src="..."}` と `:::video[https://...]` の2形を許す
        const url =
          (typeof src === "string" && src.trim()) ||
          (typeof label === "string" && label.trim()) ||
          "";
        if (!url) return null;
        return (
          <VideoEmbed
            src={url}
            title={typeof title === "string" ? title : undefined}
            poster={typeof poster === "string" ? poster : undefined}
            onPlay={() => onVideoPlayRef.current?.(url)}
          />
        );
      },
      // `:::photo{src="..."}` に本文を1行入れるとキャプションになる。
      // 素の `![](url)` と分けているのは、キャプションを持てるかどうかが違うから。
      // note から持ってきた取材写真のように、写真そのものより
      // 「これは何の写真か」の一言が要るものはこちらを使う。
      "directive-photo": ({ src, alt, children }: any) => {
        const url = typeof src === "string" ? src.trim() : "";
        // 本文は DB から来るので、出所を自分のところに限る。
        // ここを緩めると記事本文から任意のホストの画像を貼れてしまう
        if (!/^https:\/\/(storage\.chotdekiru\.com|yumesaku\.tech)\/[\w./-]+$/.test(url)) {
          return null;
        }
        const caption = figureIdFromChildren(children).trim();
        return (
          <figure className="my-8">
            <img
              src={url}
              alt={typeof alt === "string" && alt ? alt : caption}
              className="block mx-auto max-w-sm w-full h-auto rounded-lg shadow-xs cursor-zoom-in transition-opacity hover:opacity-90"
              loading="lazy"
              onClick={() => setLightboxSrc(url)}
            />
            {caption ? (
              <figcaption className="mt-2 text-center text-xs leading-5 text-sub">
                {caption}
              </figcaption>
            ) : null}
          </figure>
        );
      },
      "directive-figure": ({ label, src, predict, fallback, children }: any) => {
        // `::figure{src="..."}` / `:::figure[...]` / `::figure[...]` の3形を許す
        const fromChildren = figureIdFromChildren(children).trim();
        const id =
          (typeof src === "string" && src) ||
          (typeof label === "string" && label) ||
          fromChildren;
        // 図解 id は `/figures/{id}.js` のパスになるので厳しく検証する
        if (typeof id !== "string" || !/^[a-z0-9-]{1,64}$/.test(id)) return null;
        return (
          <InteractiveFigure
            src={id}
            // 図解は本文の先頭に置くので、既定では予測クイズを挟まない。
            // まだ一行も読んでいない人に出題すると、答えられない問いを
            // 突きつけるだけになる。触れることが図解の売りなので先に触らせる
            predict={predict === "on" ? "on" : "off"}
            fallback={typeof fallback === "string" ? fallback : undefined}
            priority={id === leadingFigureId}
          />
        );
      },
      "directive-tweet": ({ id, children }: any) => {
        const tweetId =
          tweetIdFromRef(typeof id === "string" ? id : null) ??
          tweetIdFromRef(figureIdFromChildren(children));
        if (!tweetId) return null;
        const post = tweets?.[tweetId];
        // null は消えた投稿。本人が消したものを記事に残さない
        if (post === null) return null;
        if (post) return <TweetEmbed post={post} />;
        return <TweetEmbedFallback url={`https://x.com/i/status/${tweetId}`} />;
      },
      // 体験ブロック（5日チャレンジ用）
      "directive-experience": ({ label }: any) => {
        const validTypes = ["day1", "day2", "day3", "day4", "day5"];
        if (label && validTypes.includes(label)) {
          return <ExperienceBlock type={label as "day1" | "day2" | "day3" | "day4" | "day5"} />;
        }
        return null;
      },
      // マーカー（ハイライト）- 下1/3のみ
      mark: ({ children }: any) => (
        <mark
          style={{
            background:
              "linear-gradient(transparent 60%, var(--color-primary-100) 60%)",
            color: "inherit",
            fontWeight: "bold",
          }}
        >
          {children}
        </mark>
      ),
    };
  }, [
    contributorMap,
    deferOffscreenCodeRendering,
    deferRunnableEditors,
    leadingFigureId,
    lockRunnable,
    renderFaqQuestion,
    tweets,
  ]);

  // ==マーカー== を前処理（useMemoでキャッシュ）
  // enableSourceMap が有効なときは前処理をスキップして、ブロックの offset が
  // 元の content と一致するようにする (== や ✅ などの装飾は失われるが
  // インライン編集モードでは正本の Markdown ソース整合性を優先する)。
  const processedContent = useMemo(() => {
    // svg-figures は本文に手を入れない。図の塊の中の空行は、書き出す側 (convert.py) が詰めてある
    // (ここで詰めると、コードの塊に書いた図の例の空行まで消える)
    if (enableSourceMap) return content;
    if (!allowRawHtml || allowRawHtml === "svg-figures") return escapeDunders(content);
    return preprocessHighlight(content);
  }, [allowRawHtml, content, enableSourceMap]);

  // remarkBlockSourceMap は ReactMarkdown 内で transform するたびに onBuilt を呼ぶ。
  // そのまま親の setState に繋ぐと「setState → 再レンダー → transform → setState」で
  // 無限ループする。content が同じなら blocks も同じはずなので、署名が変わった
  // ときだけ親へ通知する。
  const lastBlocksSignatureRef = useRef<string | null>(null);
  const handleSourceMapBuilt = useMemo(() => {
    if (!enableSourceMap || !onSourceMapBuilt) return undefined;
    return (blocks: MarkdownBlockSource[]) => {
      const sig = blocks
        .map((b) => `${b.index}:${b.startOffset}:${b.endOffset}`)
        .join("|");
      if (sig === lastBlocksSignatureRef.current) return;
      lastBlocksSignatureRef.current = sig;
      onSourceMapBuilt(blocks);
    };
  }, [enableSourceMap, onSourceMapBuilt]);

  // content が変わったら署名キャッシュをリセット (新しい blocks を確実に通知)
  useEffect(() => {
    lastBlocksSignatureRef.current = null;
  }, [processedContent]);

  // :::runnable ブロックの抽出は本文サイズに比例する regex スキャンなので、
  // remarkPlugins の毎回再生成と分離して memo 化する。
  const runnableSources = useMemo(
    () => extractRunnableSources(processedContent),
    [processedContent],
  );

  const allComponents = useMemo(
    () => (extraComponents ? { ...markdownComponents, ...extraComponents } : markdownComponents),
    [markdownComponents, extraComponents],
  );

  // remarkPlugins / rehypePlugins は ReactMarkdown 内 processor が
  // identity を cache key にしているため、配列・option object とも毎レンダー
  // 新規参照だとパイプライン全体が再実行される。useMemo で固定する。
  const remarkPlugins = useMemo(() => {
    const base: any[] = [
      remarkGfm,
      // 自動リンクが空白の無い日本語を飲み込まないよう、URL の字までに止める
      remarkAsciiAutolink,
      remarkDirective,
      // 地の文の <Profile /> や Array<string> を HTML のタグとして食わず、字のまま出す
      remarkProseTagsAsText,
      [remarkCustomDirectives, { runnableSources }],
    ];
    if (readingTips) base.push(remarkExampleKinds, remarkReadingTips);
    if (foldQuizAnswers) base.push(remarkQuizAnswers);
    if (enableSourceMap) {
      base.push(remarkMarkSyntax as any);
      base.push([
        remarkBlockSourceMap,
        { source: processedContent, onBuilt: handleSourceMapBuilt },
      ] as any);
    }
    return base;
  }, [
    processedContent,
    enableSourceMap,
    handleSourceMapBuilt,
    runnableSources,
    readingTips,
    foldQuizAnswers,
  ]);

  const rehypePlugins = useMemo<any[]>(() => {
    const plugins: any[] = [];
    if (allowRawHtml === "svg-figures") plugins.push(rehypeSvgFigures);
    else if (allowRawHtml) plugins.push(rehypeRaw);
    plugins.push(rehypeSanitizeLessonHtml);
    // 本文の語 (Python、React など) の前に正式ロゴを置く。markdown を HTML にする経路では常に通す。
    // 形は外部の svg を指すだけで、ロゴの語が無い本文には何も足さず何も読み込まない。
    // 用語集より先に置く。語がまだ素のテキストのうちにロゴを前に足し、
    // 用語集は svg を飛ばすので、ロゴの後ろの語にはそのまま下線が付く
    plugins.push(rehypeInlineLogos);
    if (glossaryMatcher) {
      plugins.push([
        rehypeGlossary,
        { matcher: glossaryMatcher, allEntries: glossary, scope: glossaryScope, maxPerBlock: marksPerBlock },
      ]);
    }
    // 考え方の語 (CPU、メモリなど) の墨の印は、用語集のあとに置く。用語集の照合は字のノードごとに行うので、
    // 先に字を割ると「仮想メモリ」の下線が消え、「主記憶装置」の中の「主記憶」に下線が付いてしまう。
    // 用語集の語の前に印が付き、下線と説明は残る (印が前、下線は字に付く)
    // 前の描き手の印を渡された描き手 (項目の頁) は、語の前の印も頁で 1 回 (節ごとに付け直さない)
    plugins.push(marksSeen ? [rehypeConceptMarks, { seen: marksSeen.marks, scope: "page" }] : rehypeConceptMarks);
    // 段落ごとの印の上限と、題の語の印を外す。用語集の下線を字に戻す前に走らせる (SSR と hydrate 後で同じ印になる)
    if (marksPerBlock !== undefined || markFreeTitle) {
      plugins.push([rehypeLimitInlineMarks, { perBlock: marksPerBlock, title: markFreeTitle }]);
    }
    if (glossaryMatcher && !hydrated) plugins.push(rehypeUnwrapGlossary);
    // 用語リンクや太字の前後に書き手が置いた空きのうち、和文どうしの間のものを詰める (用語の変換のあとで走らせる)
    plugins.push(rehypeJoinCjkAcrossInline);
    // 段落の最後の 4 字を折り返さない塊にして、最後の行が 1〜3 字だけにならないようにする (空きを詰めたあとで走らせる)
    plugins.push(rehypeKeepTail);
    // 短い「…」(12 字以内) を折らない塊にする (「数値 / B を出す」と割れた)。数と単位より先に走らせ、「3 回」を含む引用も 1 つにする
    plugins.push(rehypeKeepQuotes);
    // 「第5章」「4本」の数と単位を折らない塊にする (「の第 / 5章」と折れた)
    plugins.push(rehypeKeepNumberUnits);
    // 呼び出し側の区切り方で文節の切れ目に wbr を入れる (塊を作ったあとで走らせ、塊の中は割らない)
    if (phraseSegment) plugins.push([rehypePhraseBreaks, { segment: phraseSegment }]);
    return plugins;
  }, [allowRawHtml, glossaryMatcher, glossary, glossaryScope, marksPerBlock, marksSeen, markFreeTitle, hydrated, phraseSegment]);

  return (
    <div
      className={`prose prose-sm md-fs-floor max-w-none ${className} ${
        deferOffscreenCodeRendering
          ? "[&_[data-code-highlight-state]]:[content-visibility:auto] [&_[data-code-highlight-state]]:[contain-intrinsic-block-size:auto_420px] [&_[data-runnable-code-block]]:[content-visibility:auto] [&_[data-runnable-code-block]]:[contain-intrinsic-block-size:auto_430px]"
          : ""
      }`.trim()}
      data-markdown-offscreen-code-rendering={
        deferOffscreenCodeRendering ? "true" : undefined
      }
    >
      <ReactMarkdown
        components={allComponents}
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
      >
        {processedContent}
      </ReactMarkdown>
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 cursor-zoom-out"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
          aria-label="拡大画像"
        >
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            aria-label="閉じる"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-800 flex items-center justify-center text-xl font-bold shadow"
          >
            ×
          </button>
          {lightboxSrc.startsWith("data:image/svg") || lightboxSrc.startsWith("blob:") ? (
            <img
              src={lightboxSrc}
              alt="拡大表示"
              className="max-w-[95vw] max-h-[95vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={lightboxSrc}
              alt="拡大表示"
              className="max-w-[95vw] max-h-[95vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
}
